import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "./lib/ExecutiveChart";
import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Building2,
  Database,
  DollarSign,
  ExternalLink,
  Gauge,
  Gamepad2,
  LineChart,
  Pause,
  Play,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Users,
  WalletCards,
  Wifi,
  WifiOff,
} from "lucide-react";
import type { DashboardPayload, Metric } from "./lib/types";
import { buildForecast, monthlyAverage } from "./lib/forecast";
import type { ForecastResult } from "./lib/forecast";
import {
  buildQuarterlyForecast,
  INTERNATIONAL_GDP_COMPOSITION_2024,
  reconcileComposition,
} from "./lib/gdpComposition";
import { fallbackDashboard } from "./data/fallback";
import { TerritorialPanel } from "./components/TerritorialPanel";
import { IntelligencePanel } from "./components/IntelligencePanel";
import { Motion3DSuite } from "./components/Motion3DSuite";

const UploadWorkbench = lazy(() => import("./components/UploadWorkbench").then((module) => ({ default: module.UploadWorkbench })));

const tabs = [
  "Resumen Ejecutivo",
  "Precios y Costos",
  "Dólar y Finanzas",
  "Proyecciones",
  "Laboratorio",
  "Análisis Visual",
  "Motion & 3D",
  "PyME y Provincias",
  "Inteligencia y Alertas",
  "Carga de datos",
  "Calendario",
  "Fuentes",
];
const chartText = {
  color: "#cbd5e1",
  fontFamily: "Inter, ui-sans-serif, system-ui",
  fontSize: 12,
};
const axis = {
  axisLine: { lineStyle: { color: "rgba(148,163,184,.2)" } },
  axisLabel: chartText,
  splitLine: { lineStyle: { color: "rgba(148,163,184,.10)" } },
};
const tooltip = {
  trigger: "axis",
  backgroundColor: "rgba(5,7,13,.96)",
  borderColor: "rgba(34,211,238,.45)",
  textStyle: { color: "#f8fafc" },
};
const neonLine = (color: string, dashed = false) => ({
  width: 3,
  color,
  type: dashed ? "dashed" : "solid",
  shadowBlur: 14,
  shadowColor: color,
  shadowOffsetY: 0,
});

function formatDate(value: string) {
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/Argentina/Cordoba",
  }).format(new Date(value));
}

function metricColor(id: string) {
  if (id.includes("fx") || id.includes("rem")) return "emerald";
  if (id.includes("ipim") || id.includes("sales")) return "rose";
  if (id.includes("ipc")) return "cyan";
  if (id.includes("cba")) return "gold";
  return "violet";
}

function IconFor({ metric }: { metric: Metric }) {
  if (metric.id.includes("fx") || metric.id.includes("rem"))
    return <DollarSign />;
  if (metric.value < 0) return <TrendingDown />;
  return <TrendingUp />;
}

function KpiCard({ metric, index }: { metric: Metric; index: number }) {
  const history = metric.history ?? [];
  const spark = history.map((point) => point.value);
  const min = Math.min(...spark, 0);
  const max = Math.max(...spark, 1);
  const points = spark
    .map(
      (value, i) =>
        `${(i / Math.max(1, spark.length - 1)) * 100},${34 - ((value - min) / Math.max(0.1, max - min)) * 28}`,
    )
    .join(" ");
  return (
    <motion.article
      className={`kpi-card ${metricColor(metric.id)}`}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.055 }}
    >
      <div className="kpi-top">
        <span className="kpi-icon">
          <IconFor metric={metric} />
        </span>
        <span className={`data-badge ${metric.status.toLowerCase()}`}>
          {metric.status}
        </span>
      </div>
      <motion.strong
        className="kpi-value"
        key={metric.display}
        initial={{ opacity: 0.4 }}
        animate={{ opacity: 1 }}
      >
        {metric.display}
      </motion.strong>
      <span className="kpi-label">{metric.label}</span>
      <span className="kpi-period">
        {metric.period} · {metric.unit}
      </span>
      {spark.length > 1 && (
        <svg
          className="spark"
          viewBox="0 0 100 36"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <polyline points={points} />
        </svg>
      )}
      <a
        href={metric.sourceUrl}
        target="_blank"
        rel="noreferrer"
        className="source-link"
      >
        {metric.source}
        <ExternalLink size={12} />
      </a>
    </motion.article>
  );
}

function Panel({
  title,
  kicker,
  children,
  wide = false,
  icon,
}: {
  title: string;
  kicker?: string;
  children: React.ReactNode;
  wide?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <section className={`panel ${wide ? "panel-wide" : ""}`}>
      <div className="panel-title">
        <div>
          {kicker && <span>{kicker}</span>}
          <h2>
            {icon}
            {title}
          </h2>
        </div>
        <button className="ghost-button" aria-label="Abrir detalle">
          <ChevronRight />
        </button>
      </div>
      {children}
    </section>
  );
}

function SectionIntro({
  eyebrow,
  title,
  description,
  decision,
}: {
  eyebrow: string;
  title: string;
  description: string;
  decision: string;
}) {
  return (
    <section className="section-intro">
      <div>
        <span>{eyebrow}</span>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <aside>
        <small>PARA DECIDIR</small>
        <b>{decision}</b>
      </aside>
    </section>
  );
}

function DecisionCard({
  status,
  title,
  evidence,
  action,
  tone,
}: {
  status: string;
  title: string;
  evidence: string;
  action: string;
  tone: "watch" | "risk" | "ok";
}) {
  return (
    <article className={`decision-card ${tone}`}>
      <span>{status}</span>
      <h3>{title}</h3>
      <p>{evidence}</p>
      <b>{action}</b>
    </article>
  );
}

function Insight({
  what,
  why,
  action,
  tone = "cyan",
}: {
  what: string;
  why: string;
  action: string;
  tone?: string;
}) {
  return (
    <div className={`insight ${tone}`}>
      <div>
        <span>Qué está pasando</span>
        <b>{what}</b>
      </div>
      <div>
        <span>Por qué importa</span>
        <b>{why}</b>
      </div>
      <div>
        <span>Decisión sugerida</span>
        <b>{action}</b>
      </div>
    </div>
  );
}

function ChartContract({
  x,
  y,
  encoding,
  period,
  sources,
  caution,
}: {
  x: string;
  y: string;
  encoding: string;
  period: string;
  sources: string;
  caution: string;
}) {
  return (
    <div className="chart-contract" aria-label="Cómo leer este gráfico">
      <div>
        <small>HORIZONTAL</small>
        <b>{x}</b>
      </div>
      <div>
        <small>VERTICAL</small>
        <b>{y}</b>
      </div>
      <div>
        <small>COLORES Y MOVIMIENTO</small>
        <b>{encoding}</b>
      </div>
      <div>
        <small>PERÍODO CUBIERTO</small>
        <b>{period}</b>
      </div>
      <div>
        <small>DATOS Y FUENTES</small>
        <b>{sources}</b>
      </div>
      <div>
        <small>IMPORTANTE</small>
        <b>{caution}</b>
      </div>
    </div>
  );
}

const forecastFormulas = {
  NAIVE: "ŷₜ₊ₕ = yₜ",
  DRIFT: "ŷₜ₊ₕ = yₜ + h·(yₜ−y₁)/(t−1)",
  HOLT_DAMPED: "ŷₜ₊ₕ = ℓₜ + (Σⱼ₌₁ʰ φʲ)·bₜ",
} as const;

function ForecastAudit({
  result,
  unit,
}: {
  result: ForecastResult;
  unit: string;
}) {
  return (
    <div className="forecast-audit">
      <div className={`audit-status ${result.validationStatus.toLowerCase()}`}>
        <ShieldCheck />
        <div>
          <small>ESTADO DE EVIDENCIA</small>
          <b>{result.validationStatus}</b>
          <span>
            {result.backtestCount} errores fuera de muestra; se requieren al
            menos {result.minimumBacktestsForCalibration} para evaluar cobertura
            de intervalos con estabilidad.
          </span>
        </div>
      </div>
      <div
        className="candidate-table"
        role="table"
        aria-label="Comparación de modelos predictivos"
      >
        <div className="candidate-head" role="row">
          <span>MODELO Y FÓRMULA</span>
          <span>MAE</span>
          <span>RMSE</span>
          <span>sMAPE</span>
          <span>SESGO</span>
          <span>DECISIÓN</span>
        </div>
        {result.candidates.map((candidate) => (
          <div className="candidate-row" role="row" key={candidate.id}>
            <span>
              <b>{candidate.label}</b>
              <code>{forecastFormulas[candidate.id]}</code>
            </span>
            <span>
              {candidate.mae.toFixed(3)} {unit}
            </span>
            <span>
              {candidate.rmse.toFixed(3)} {unit}
            </span>
            <span>{candidate.smape.toFixed(2)}%</span>
            <span>
              {candidate.bias.toFixed(3)} {unit}
            </span>
            <strong>
              {candidate.id === result.modelId ? "GANADOR" : "CHALLENGER"}
            </strong>
          </div>
        ))}
      </div>
      <div className="audit-notes">
        <p>
          <b>Selección:</b> mínimo RMSE en origen rodante. Mejora frente a
          Naive: <strong>{result.improvementVsNaivePct.toFixed(1)}%</strong>; si
          es 0%, el modelo simple sigue siendo la referencia correcta.
        </p>
        <p>
          <b>Incertidumbre:</b> {result.intervalMethod},{" "}
          {result.simulations.toLocaleString("es-AR")} trayectorias, semilla{" "}
          {result.seed} y ancho kernel {result.residualBandwidth.toFixed(3)} {unit}.
          Las bandas son percentiles P10–P90 y P2,5–P97,5; con sólo{" "}
          {result.backtestCount} errores se informan como provisionales, no calibradas.
        </p>
      </div>
    </div>
  );
}

function QuantitativeModelRegistry() {
  const rows = [
    [
      "Naive · Deriva · Holt amortiguado",
      "ACTIVO",
      "12 observaciones mensuales + origen rodante",
      "Pronóstico central; gana el menor RMSE",
    ],
    [
      "Bootstrap residual Monte Carlo",
      "ACTIVO PROVISIONAL",
      "10.000 trayectorias · semilla 2030 · residuos fuera de muestra",
      "Bandas simuladas; cobertura aún no calibrada",
    ],
    [
      "SARIMA · Estado-espacio/Kalman",
      "NO ELEGIBLE HOY",
      "≥36 meses, estacionalidad y residuos diagnosticables",
      "Recién se activa si supera a Naive fuera de muestra",
    ],
    [
      "VAR/BVAR · MIDAS",
      "NO ELEGIBLE HOY",
      "Series macro alineadas, vintages y más historia",
      "Cruces macro sin fuga temporal",
    ],
    [
      "Árboles no paramétricos · TCN · Red bayesiana dinámica",
      "NO ELEGIBLE HOY",
      "Muestra de entrenamiento mucho mayor y validación temporal anidada",
      "No se usa deep learning con 12 filas",
    ],
    [
      "Minsky · Dinámica de sistemas",
      "CAPA DE ESCENARIOS",
      "Stocks, flujos, deuda, tasas y ecuaciones causales documentadas",
      "Estrés estructural; no reemplaza un pronóstico estadístico",
    ],
    [
      "Redes de información · Kyle",
      "SIN DATOS DE ENTRADA",
      "Exposiciones bilaterales u órdenes/flujo de mercado",
      "No se calcula con agregados macro",
    ],
    [
      "Decisión ‘cuántica’",
      "EXPERIMENTAL · NO ACTIVA",
      "Evidencia comparativa y mejora fuera de muestra",
      "No se presenta como aumento de precisión",
    ],
  ];
  return (
    <div className="model-registry">
      <div className="registry-head">
        <span>MÉTODO</span>
        <span>ESTADO</span>
        <span>CONDICIÓN CIENTÍFICA</span>
        <span>USO EN ESTE TABLERO</span>
      </div>
      {rows.map(([model, status, gate, use]) => (
        <div className="registry-row" key={model}>
          <b>{model}</b>
          <em
            className={
              status.startsWith("ACTIVO")
                ? "enabled"
                : status.startsWith("CAPA")
                  ? "scenario"
                  : "blocked"
            }
          >
            {status}
          </em>
          <span>{gate}</span>
          <span>{use}</span>
        </div>
      ))}
    </div>
  );
}

function FormulaLedger() {
  const formulas = [
    [
      "MAE",
      "(1/n)·Σ|yₜ−ŷₜ|",
      "Error absoluto medio; misma unidad del indicador.",
    ],
    [
      "RMSE",
      "√[(1/n)·Σ(yₜ−ŷₜ)²]",
      "Penaliza más los errores grandes; criterio de selección.",
    ],
    [
      "sMAPE",
      "(100/n)·Σ 2|yₜ−ŷₜ|/(|yₜ|+|ŷₜ|)",
      "Error porcentual simétrico; se protege el divisor cercano a cero.",
    ],
    [
      "Sesgo",
      "(1/n)·Σ(ŷₜ−yₜ)",
      "Positivo = sobreestima; negativo = subestima.",
    ],
    [
      "Bootstrap suavizado",
      "ŷₜ₊ₕˢ = ŷₜ₊ₕ + Σ(ε*ⱼ + b·zⱼ)",
      "ε* se remuestrea de residuos centrados; z~N(0,1) y b usa la regla robusta de Silverman.",
    ],
    [
      "Bandas",
      "P10/P90 y P2,5/P97,5 de {ŷˢ}",
      "Percentiles simulados 80% y 95%; no certeza.",
    ],
  ];
  return (
    <div className="formula-ledger">
      {formulas.map(([name, formula, meaning]) => (
        <article key={name}>
          <small>{name}</small>
          <code>{formula}</code>
          <p>{meaning}</p>
        </article>
      ))}
    </div>
  );
}

function SignalCoverageTable({ data }: { data: DashboardPayload }) {
  const get = (id: string) => data.metrics.find((item) => item.id === id);
  const rows = [
    {
      id: "ipc",
      signal: "IPC",
      reading: "Variación de precios del mes",
      projection: "Ago–dic: sólo modelo validado",
    },
    {
      id: "real_wage",
      signal: "Salario real",
      reading: "Salario nominal descontando IPC",
      projection: "No proyectado",
    },
    {
      id: "debt_income",
      signal: "Deuda / ingreso",
      reading: "Carga de deuda sobre ingreso disponible",
      projection: "Bloqueado: no existe serie mensual homogénea",
    },
    {
      id: "delinquency",
      signal: "Morosidad",
      reading: "Crédito familiar en situación irregular",
      projection: "No proyectado",
    },
    {
      id: "sales",
      signal: "Consumo PyME",
      reading: "Ventas reales contra igual mes de 2025",
      projection: "No proyectado",
    },
    {
      id: "activity",
      signal: "Actividad",
      reading: "Cambio mensual desestacionalizado EMAE",
      projection: "No proyectado",
    },
    {
      id: "investment",
      signal: "Inversión",
      reading: "FBCF real contra igual trimestre de 2025",
      projection: "No se mensualiza ni interpola",
    },
  ];
  return (
    <div
      className="signal-coverage"
      aria-label="Cobertura y estado de las siete señales"
    >
      <div className="signal-head">
        <span>SEÑAL</span>
        <span>QUÉ MIDE</span>
        <span>ÚLTIMO DATO DISPONIBLE</span>
        <span>HACIA DICIEMBRE</span>
        <span>FUENTE</span>
      </div>
      {rows.map((row) => {
        const item = get(row.id);
        return (
          <div className="signal-row" key={row.id}>
            <b>{row.signal}</b>
            <span>{row.reading}</span>
            <span>
              {item ? (
                <>
                  {item.display}
                  <small>
                    {item.period} · {item.status}
                  </small>
                </>
              ) : (
                <>
                  SIN DATO<small>Serie no cargada</small>
                </>
              )}
            </span>
            <span>{row.projection}</span>
            {item ? (
              <a href={item.sourceUrl} target="_blank" rel="noreferrer">
                {item.source}
                <ExternalLink />
              </a>
            ) : (
              <span>—</span>
            )}
          </div>
        );
      })}
      <p>
        <ShieldCheck /> <b>Probabilidad conjunta a diciembre: NO ESTIMADA.</b>{" "}
        Publicarla con frecuencias y cortes distintos produciría una precisión
        ficticia. El tablero volverá a evaluar esta decisión cuando exista una
        muestra contemporánea suficiente.
      </p>
    </div>
  );
}

function DataSufficiencyTable({ data }: { data: DashboardPayload }) {
  const ids = ["ipc", "ipim", "real_wage", "activity", "exports", "imports", "trade_balance", "base_money", "sales", "delinquency", "investment"];
  return (
    <div className="signal-coverage" aria-label="Auditoría de suficiencia de datos">
      <div className="signal-head">
        <span>VARIABLE</span><span>FRECUENCIA Y UNIDAD</span><span>COBERTURA CARGADA</span><span>USO PREDICTIVO</span><span>FUENTE</span>
      </div>
      {ids.map((id) => {
        const item = data.metrics.find((metric) => metric.id === id);
        const history = item?.history ?? [];
        const monthly = item?.frequency === "DIARIA" ? monthlyAverage(history) : history;
        const eligible = item?.frequency === "MENSUAL" && monthly.length >= 30;
        return (
          <div className="signal-row" key={id}>
            <b>{item?.label ?? id}</b>
            <span>{item ? `${item.frequency ?? "s/d"} · ${item.unit}` : "SIN CONTRATO"}</span>
            <span>{monthly.length ? <>{monthly.length} observaciones<small>{monthly[0]?.date} → {monthly.at(-1)?.date}</small></> : <>SIN HISTORIA<small>No se completa</small></>}</span>
            <span>{eligible ? "ELEGIBLE PARA CHALLENGER; debe ganar backtest" : item?.frequency === "MENSUAL" ? "NO ELEGIBLE: requiere ≥30 meses" : "NO SE MENSUALIZA AUTOMÁTICAMENTE"}</span>
            {item ? <a href={item.sourceUrl} target="_blank" rel="noreferrer">{item.source}<ExternalLink /></a> : <span>—</span>}
          </div>
        );
      })}
      <p><ShieldCheck /> El modelo no recibe una variable sólo porque exista: primero verifica frecuencia, cobertura, rezago, unidad y disponibilidad en el instante histórico del pronóstico. Esto evita fuga temporal y falsa precisión.</p>
    </div>
  );
}

function GdpCompositionPanel({ data }: { data: DashboardPayload }) {
  const share = (id: string) => data.metrics.find((metric) => metric.id === id);
  const c = share("gdp_share_c");
  const g = share("gdp_share_g");
  const i = share("gdp_share_i");
  const xn = share("gdp_share_xn");
  const current = c && g && i && xn
    ? reconcileComposition(c.value, g.value, i.value, xn.value, c.date, "REAL")
    : null;
  const projections = useMemo(() => {
    try {
      if (!c?.history || !g?.history || !i?.history || !xn?.history) return null;
      return {
        c: buildQuarterlyForecast(c.history, 3),
        g: buildQuarterlyForecast(g.history, 3),
        i: buildQuarterlyForecast(i.history, 3),
        xn: buildQuarterlyForecast(xn.history, 3),
      };
    } catch {
      return null;
    }
  }, [c?.history, g?.history, i?.history, xn?.history]);
  const projected = projections
    ? reconcileComposition(
        projections.c.forecast.at(-1)!.value,
        projections.g.forecast.at(-1)!.value,
        projections.i.forecast.at(-1)!.value,
        projections.xn.forecast.at(-1)!.value,
        "2026-Q4",
        "MODELO",
      )
    : null;
  const rows = [
    ...INTERNATIONAL_GDP_COMPOSITION_2024.map((row) => ({ ...row, label: `${row.country} · ${row.period}`, status: "OBSERVADO COMPARABLE" })),
    ...(current ? [{ ...current, country: "Argentina", period: current.date, label: `Argentina · ${current.date}`, status: "OBSERVADO INDEC", source: "INDEC · Cuentas nacionales", sourceUrl: c?.sourceUrl ?? "https://www.indec.gob.ar/indec/web/Nivel4-Tema-3-9-47" }] : []),
    ...(projected ? [{ ...projected, country: "Argentina", period: "2026-Q4", label: "Argentina · 2026-Q4", status: "PROYECCIÓN CONDICIONADA", source: "Modelo MONTIEL sobre series INDEC", sourceUrl: c?.sourceUrl ?? "https://www.indec.gob.ar/indec/web/Nivel4-Tema-3-9-47" }] : []),
  ];
  const option = {
    aria: { enabled: true, decal: { show: true }, description: "Composición del PIB por gasto de Suecia, Japón y Argentina, con dato observado y proyección argentina reconciliada." },
    animationDuration: 900,
    tooltip: {
      ...tooltip,
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params: Array<{ seriesName: string; value: number; dataIndex: number }>) => {
        const row = rows[params[0]?.dataIndex];
        return `<b>${row?.label ?? ""}</b><br/>${params.map((item) => `${item.seriesName}: ${Number(item.value).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`).join("<br/>")}<br/><span style="color:#94a3b8">${row?.status ?? ""}</span>`;
      },
    },
    legend: { top: 0, textStyle: chartText },
    grid: { left: 74, right: 28, top: 58, bottom: 98 },
    xAxis: { type: "category", data: rows.map((row) => row.label.replace(" · ", "\n")), ...axis, axisLabel: { ...chartText, interval: 0, lineHeight: 17 } },
    yAxis: { type: "value", name: "% del PIB corriente", min: -12, max: 108, nameLocation: "middle", nameGap: 52, ...axis, nameTextStyle: chartText, axisLabel: { ...chartText, formatter: "{value}%" } },
    series: [
      ["C · Consumo privado", "c", "#22d3ee"],
      ["G · Consumo público", "g", "#8b5cf6"],
      ["I · Capital + existencias", "i", "#f4b942"],
      ["XN · Exportaciones netas", "xn", "#39ffb6"],
      ["R · Residual estadístico", "residual", "#ff3f75"],
    ].map(([name, key, color]) => ({
      name,
      type: "bar",
      stack: "identidad",
      barMaxWidth: 70,
      data: rows.map((row) => row[key as keyof typeof row] as number),
      itemStyle: { color, borderColor: "rgba(255,255,255,.35)", borderWidth: 1, shadowBlur: 12, shadowColor: color, borderRadius: 3 },
      emphasis: { focus: "series" },
    })),
  };
  const historyDates = c?.history?.map((point) => point.date) ?? [];
  const projectedDates = projections?.c.forecast.map((point) => point.date) ?? [];
  const historicalMetric = (item: Metric | undefined) => historyDates.map((date) => item?.history?.find((point) => point.date === date)?.value ?? null);
  const historyOption = {
    aria: { enabled: true, decal: { show: true }, description: "Evolución trimestral de la composición del PIB argentino entre 2019 y 2026, con proyección diferenciada." },
    animationDuration: 1000,
    tooltip: {
      ...tooltip,
      trigger: "axis",
      formatter: (params: Array<{ seriesName: string; value: number; axisValue: string; marker: string }>) =>
        `<b>${params[0]?.axisValue ?? ""}</b><br/>${params
          .filter((item) => item.value != null && Number.isFinite(Number(item.value)))
          .map((item) => `${item.marker}${item.seriesName}: ${Number(item.value).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`)
          .join("<br/>")}`,
    },
    legend: { top: 0, textStyle: chartText },
    grid: { left: 68, right: 32, top: 56, bottom: 66 },
    xAxis: { type: "category", data: [...historyDates, ...projectedDates], boundaryGap: false, name: "Trimestre →", nameLocation: "middle", nameGap: 42, ...axis, axisLabel: { ...chartText, interval: 3 } },
    yAxis: { type: "value", name: "% del PIB corriente", nameLocation: "middle", nameGap: 50, ...axis, nameTextStyle: chartText, axisLabel: { ...chartText, formatter: "{value}%" } },
    series: [
      ["C observado", c, "#22d3ee", projections?.c],
      ["G observado", g, "#8b5cf6", projections?.g],
      ["I observado", i, "#f4b942", projections?.i],
      ["XN observado", xn, "#39ffb6", projections?.xn],
    ].flatMap(([name, item, color, projection]) => {
      const observed = historicalMetric(item as Metric | undefined);
      const projectedValues = (projection as ReturnType<typeof buildQuarterlyForecast> | undefined)?.forecast.map((point) => point.value) ?? [];
      const bridge = observed.at(-1) ?? null;
      return [
        { name, type: "line", data: [...observed, ...projectedDates.map(() => null)], smooth: .15, symbolSize: 5, lineStyle: neonLine(String(color)), itemStyle: { color: "#fff", borderColor: color, borderWidth: 2 } },
        { name: `${String(name).replace(" observado", "")} modelo`, type: "line", data: [...historyDates.slice(0, -1).map(() => null), bridge, ...projectedValues], smooth: .15, symbolSize: 6, lineStyle: neonLine(String(color), true), itemStyle: { color: "#05070d", borderColor: color, borderWidth: 2 } },
      ];
    }),
  };
  const q4Ranges = projections ? [
    ["C", projections.c], ["G", projections.g], ["I", projections.i], ["XN", projections.xn],
  ] as const : [];
  const developedReference = INTERNATIONAL_GDP_COMPOSITION_2024.filter((row) => row.country !== "Argentina");
  const referenceInvestment = developedReference.reduce((sum, row) => sum + row.i, 0) / developedReference.length;
  return (
    <>
      <ReactECharts option={option} style={{ height: 535 }} />
      <ChartContract
        x="País y corte comparable indicado debajo de cada barra."
        y="Participación de cada componente en el PIB a precios corrientes (%). Una sección negativa baja de cero."
        encoding="C cian: consumo privado · G violeta: consumo público · I oro: capital y existencias · XN verde: exportaciones netas · R rosa: residual."
        period="Suecia y Japón: 2024 homogéneo Banco Mundial; Argentina: último dato oficial y escenario Q4 claramente separados."
        sources="Banco Mundial WDI + INDEC Oferta y Demanda Globales."
        caution="Una participación mayor no significa automáticamente mayor bienestar. Los períodos diferentes no se presentan como comparación exacta."
      />
      <div className="identity-equations">
        {rows.map((row) => {
          const total = row.c + row.g + row.i + row.xn + row.residual;
          return (
            <article key={row.label}>
              <small>{row.status}</small>
              <b>{row.label}</b>
              <code>{row.c.toFixed(1)} C + {row.g.toFixed(1)} G + {row.i.toFixed(1)} I {row.xn >= 0 ? "+" : "−"} {Math.abs(row.xn).toFixed(1)} XN {row.residual >= 0 ? "+" : "−"} {Math.abs(row.residual).toFixed(1)} R = {total.toFixed(1)}%</code>
              <a href={row.sourceUrl} target="_blank" rel="noreferrer">Fuente y definición <ExternalLink /></a>
            </article>
          );
        })}
      </div>
      {projected && projections && (
        <div className="projection-audit">
          <div>
            <small>LECTURA EJECUTIVA</small>
            <b>La distancia principal está en inversión, no en una sola décima de inflación.</b>
            <p>En el último trimestre observado, I incluye capital fijo y existencias. El centro proyectado para 2026-Q4 queda {projected.i >= referenceInvestment ? "por encima" : "por debajo"} del promedio 2024 de Suecia y Japón. Esto describe estructura de demanda; no prueba por sí solo productividad ni desarrollo.</p>
          </div>
          <div>
            <small>AUDITORÍA DEL PRONÓSTICO Q4</small>
            <b>Cuatro modelos seleccionados por backtest</b>
            <p>{q4Ranges.map(([label, result]) => `${label}: ${result.label}, RMSE ${result.rmse.toFixed(2)} pp, rango 80% ${result.forecast.at(-1)!.lower80.toFixed(1)}–${result.forecast.at(-1)!.upper80.toFixed(1)}`).join(" · ")}</p>
          </div>
          <div>
            <small>REGLA DE HONESTIDAD</small>
            <b>2026-Q4 es modelo, no dato exacto.</b>
            <p>El residual cierra la identidad a 100%. La publicación oficial del cuarto trimestre de 2026 llegará después del cierre del período y reemplazará esta estimación.</p>
          </div>
        </div>
      )}
      <div className="subchart-title">
        <small>PATRONES ARGENTINOS · SIN EXTRAPOLAR CRISIS MECÁNICAMENTE</small>
        <b>Cómo cambió la composición trimestral desde 2019 y qué continúa el modelo hasta Q4-2026</b>
        <p>Las líneas continuas son datos INDEC; las punteadas son pronósticos. La inestabilidad de existencias y residual es parte del diagnóstico, no ruido que se oculta.</p>
      </div>
      <ReactECharts option={historyOption} style={{ height: 520 }} />
      <ChartContract
        x="Trimestre calendario, desde 2019 hasta el horizonte 2026-Q4."
        y="Participación de C, G, I y XN sobre el PIB corriente (%)."
        encoding="C cian · G violeta · I oro · XN verde. Línea continua: INDEC observado; punteada: modelo; corte vertical: inicio del pronóstico."
        period="INDEC 2019-Q1 a 2026-Q1; modelo únicamente desde el último dato hasta 2026-Q4."
        sources="INDEC Oferta y Demanda Globales, cuadro 11 y series API oficiales."
        caution="No mezclar participaciones corrientes con crecimiento real. El modelo lleva bandas y error de backtest; no es un dato futuro exacto."
      />
      <div className="gdp-line-table" aria-label="Valores exactos de las líneas de composición del PIB">
        <div className="gdp-line-head"><span>TRIMESTRE</span><span>C · PRIVADO</span><span>G · PÚBLICO</span><span>I · CAPITAL/EXIST.</span><span>XN · NETAS</span><span>CLASE</span></div>
        {[...historyDates.slice(-8), ...projectedDates].map((date) => {
          const observedIndex = historyDates.indexOf(date);
          const projectedIndex = projectedDates.indexOf(date);
          const value = (item: Metric | undefined, result: ReturnType<typeof buildQuarterlyForecast> | undefined) => observedIndex >= 0 ? item?.history?.[observedIndex]?.value : result?.forecast?.[projectedIndex]?.value;
          const format = (number?: number) => number == null ? "s/d" : `${number.toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`;
          return <div className="gdp-line-row" key={date}><b>{date}</b><span>{format(value(c, projections?.c))}</span><span>{format(value(g, projections?.g))}</span><span>{format(value(i, projections?.i))}</span><span>{format(value(xn, projections?.xn))}</span><em className={observedIndex >= 0 ? "real" : "modelo"}>{observedIndex >= 0 ? "INDEC" : "MODELO"}</em></div>;
        })}
      </div>
    </>
  );
}

function ForecastChart({
  metric,
  horizon = 3,
}: {
  metric: Metric;
  horizon?: number;
}) {
  const series = useMemo(
    () =>
      metric.history
        ? metric.frequency === "DIARIA"
          ? monthlyAverage(metric.history)
          : metric.history
        : [],
    [metric],
  );
  const result = useMemo(() => {
    try {
      return buildForecast(series, horizon, "monthly");
    } catch {
      return null;
    }
  }, [series, horizon]);
  if (!result || !series.length)
    return (
      <div className="empty scientific">
        <AlertTriangle />
        <div>
          <b>PROYECCIÓN NO HABILITADA</b>
          <span>
            Se requieren 12 observaciones mensuales oficiales. No se completan
            meses con supuestos.
          </span>
        </div>
      </div>
    );
  const all = [
    ...series.map((p) => ({
      ...p,
      real: p.value,
      model: undefined as number | undefined,
      lower80: undefined as number | undefined,
      upper80: undefined as number | undefined,
      lower95: undefined as number | undefined,
      upper95: undefined as number | undefined,
    })),
    ...result.forecast.map((p) => ({
      ...p,
      real: undefined as number | undefined,
      model: p.value,
    })),
  ];
  const lower95 = all.map((p) =>
    typeof p.lower95 === "number" ? p.lower95 : null,
  );
  const width95 = all.map((p) =>
    typeof p.upper95 === "number" && typeof p.lower95 === "number"
      ? p.upper95 - p.lower95
      : null,
  );
  const lower80 = all.map((p) =>
    typeof p.lower80 === "number" ? p.lower80 : null,
  );
  const width80 = all.map((p) =>
    typeof p.upper80 === "number" && typeof p.lower80 === "number"
      ? p.upper80 - p.lower80
      : null,
  );
  const option = {
    aria: { enabled: true, decal: { show: true }, description: `${metric.label}: historia observada, pronóstico central y bandas de incertidumbre.` },
    animationDuration: 720,
    tooltip,
    legend: {
      top: 0,
      textStyle: chartText,
      data: ["Real", "Modelo", "Banda 80%", "Banda 95%"],
    },
    grid: { left: 44, right: 24, top: 46, bottom: 44 },
    xAxis: {
      type: "category",
      data: all.map((p) => p.date.slice(0, 7)),
      boundaryGap: false,
      ...axis,
    },
    yAxis: {
      type: "value",
      name: metric.unit,
      ...axis,
      nameTextStyle: chartText,
      axisLabel: {
        ...chartText,
        formatter: metric.unit.includes("ARS/USD") ? "${value}" : "{value}%",
      },
    },
    series: [
      {
        name: "Base 95",
        type: "line",
        stack: "band95",
        data: lower95,
        symbol: "none",
        lineStyle: { opacity: 0 },
        areaStyle: { opacity: 0 },
        silent: true,
      },
      {
        name: "Banda 95%",
        type: "line",
        stack: "band95",
        data: width95,
        symbol: "none",
        lineStyle: { opacity: 0 },
        areaStyle: { color: "rgba(139,92,246,.18)" },
        silent: true,
      },
      {
        name: "Base 80",
        type: "line",
        stack: "band80",
        data: lower80,
        symbol: "none",
        lineStyle: { opacity: 0 },
        areaStyle: { opacity: 0 },
        silent: true,
      },
      {
        name: "Banda 80%",
        type: "line",
        stack: "band80",
        data: width80,
        symbol: "none",
        lineStyle: { opacity: 0 },
        areaStyle: { color: "rgba(244,185,66,.24)" },
        silent: true,
      },
      {
        name: "Real",
        type: "line",
        smooth: 0.28,
        data: all.map((p) => p.real ?? null),
        symbolSize: 7,
        lineStyle: neonLine("#22d3ee"),
        itemStyle: {
          color: "#e6fbff",
          borderColor: "#22d3ee",
          borderWidth: 3,
          shadowBlur: 18,
          shadowColor: "#22d3ee",
        },
        areaStyle: { color: "rgba(34,211,238,.08)" },
      },
      {
        name: "Modelo",
        type: "line",
        smooth: 0.28,
        data: all.map((p) => p.model ?? null),
        symbolSize: 7,
        lineStyle: neonLine("#f4b942", true),
        itemStyle: {
          color: "#fff7d6",
          borderColor: "#f4b942",
          borderWidth: 3,
          shadowBlur: 18,
          shadowColor: "#f4b942",
        },
        markPoint: {
          symbol: "circle",
          symbolSize: 18,
          data: [{ type: "max", name: "Pulso" }],
          itemStyle: {
            color: "#fff",
            borderColor: "#f4b942",
            borderWidth: 5,
            shadowBlur: 30,
            shadowColor: "#f4b942",
          },
          label: { show: false },
          animation: true,
        },
      },
    ],
  };
  return (
    <>
      <ReactECharts option={option} style={{ height: 360 }} />
      <ChartContract
        x={`Mes calendario (${series[0]?.date} a ${result.forecast.at(-1)?.date})`}
        y={`${metric.label}: ${metric.unit}`}
        encoding="Cian continua: observación · Oro punteada: pronóstico central · Áreas violeta/oro: percentiles simulados 95%/80%"
        period={`Corte ${series.at(-1)?.date}; horizonte ${horizon} meses`}
        sources={`${metric.source} · observaciones ${metric.status}; motor cuantitativo MONTIEL v1.0.7`}
        caution="La línea futura es un resultado estadístico condicionado a la historia disponible. No es un dato oficial ni una certeza."
      />
      <div className="model-strip">
        <span>
          Ganador <b>{result.model}</b>
        </span>
        <span>
          Muestra <b>{result.sampleSize}</b>
        </span>
        <span>
          Backtests <b>{result.backtestCount}</b>
        </span>
        <span>
          MAE <b>{result.mae.toFixed(2)}</b>
        </span>
        <span>
          RMSE <b>{result.rmse.toFixed(2)}</b>
        </span>
        <span>
          sMAPE <b>{result.smape.toFixed(1)}%</b>
        </span>
        <span>
          Sesgo <b>{result.bias.toFixed(2)}</b>
        </span>
      </div>
      <ForecastAudit
        result={result}
        unit={metric.unit.includes("%") ? "pp" : metric.unit}
      />
      <p className="chart-disclaimer">
        Champion–challenger con origen rodante. Las bandas provienen de
        bootstrap residual reproducible y quedan marcadas{" "}
        {result.validationStatus}; no son certezas ni intervalos oficiales.
      </p>
    </>
  );
}

type LabState = {
  revenue: number;
  costs: number;
  debt: number;
  rate: number;
  inflation: number;
  fx: number;
  sales: number;
  importShare: number;
  incomeRise: number;
};

function seededRandom(seed: number) {
  let value = seed >>> 0;
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 4294967296;
  };
}

function normal(random: () => number) {
  const u = Math.max(1e-9, random());
  const v = Math.max(1e-9, random());
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  suffix = "%",
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="lab-slider">
      <span>
        {label}
        <b>
          {value.toLocaleString("es-AR")}
          {suffix}
        </b>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function DecisionLab({
  data,
  reducedMotion,
}: {
  data: DashboardPayload;
  reducedMotion: boolean;
}) {
  const live = (id: string, fallback: number) =>
    data.metrics.find((m) => m.id === id)?.value ?? fallback;
  const [state, setState] = useState<LabState>({
    revenue: 10,
    costs: 8,
    debt: 5,
    rate: live("rate", 35),
    inflation: live("ipc", 2.1),
    fx: 3,
    sales: live("sales", -3.8),
    importShare: 20,
    incomeRise: 1.5,
  });
  const set = (key: keyof LabState, value: number) =>
    setState((old) => ({ ...old, [key]: value }));
  const monthlyRate = Math.pow(1 + state.rate / 100, 1 / 12) - 1;
  const revenue =
    state.revenue * (1 + state.sales / 100) * (1 + state.inflation / 100);
  const costs =
    state.costs *
    (1 + state.inflation / 100 + (state.importShare / 100) * (state.fx / 100));
  const debtService = state.debt * monthlyRate;
  const cash = revenue - costs - debtService;
  const margin = revenue ? (cash / revenue) * 100 : 0;
  const purchasingPower = state.incomeRise - state.inflation;
  const stress = Math.max(
    0,
    Math.min(
      100,
      50 + margin * 2 - Math.max(0, -state.sales) * 2 - state.rate / 8,
    ),
  );
  const scenario =
    stress >= 70 ? "RESISTENTE" : stress >= 40 ? "VIGILAR" : "TENSIÓN";
  const tone = stress >= 70 ? "#39ffb6" : stress >= 40 ? "#f4b942" : "#ff3f75";
  const money = (value: number) =>
    `$${value.toLocaleString("es-AR", { maximumFractionDigits: 2 })} M`;
  const simulations = useMemo(() => {
    const random = seededRandom(2030);
    return Array.from({ length: 2000 }, () => {
      const i = Math.max(0, state.inflation + normal(random) * 0.75);
      const f = state.fx + normal(random) * 3.2;
      const s = state.sales + normal(random) * 4;
      const r = Math.max(0, state.rate + normal(random) * 8);
      const simulatedRevenue = state.revenue * (1 + s / 100) * (1 + i / 100);
      const simulatedCosts =
        state.costs * (1 + i / 100 + (state.importShare / 100) * (f / 100));
      const simulatedDebt = state.debt * (Math.pow(1 + r / 100, 1 / 12) - 1);
      return simulatedRevenue - simulatedCosts - simulatedDebt;
    }).sort((a, b) => a - b);
  }, [state]);
  const percentile = (p: number) =>
    simulations[
      Math.min(simulations.length - 1, Math.floor(simulations.length * p))
    ];
  const positiveProbability =
    (simulations.filter((value) => value > 0).length / simulations.length) *
    100;
  const expectedCash =
    simulations.reduce((sum, value) => sum + value, 0) / simulations.length;
  const expectedShortfall10 =
    simulations
      .slice(0, Math.max(1, Math.floor(simulations.length * 0.1)))
      .reduce((sum, value) => sum + value, 0) /
    Math.max(1, Math.floor(simulations.length * 0.1));
  const downside = Math.max(0.001, percentile(0.5) - percentile(0.1));
  const asymmetry = (percentile(0.9) - percentile(0.5)) / downside;
  const interestCoverage =
    debtService > 0
      ? Math.max(0, revenue - costs) / debtService
      : Number.POSITIVE_INFINITY;
  const breakEvenRevenue = costs + debtService;
  const safetyMargin = revenue
    ? ((revenue - breakEvenRevenue) / revenue) * 100
    : 0;
  const fxExposure = state.costs * (state.importShare / 100) * (state.fx / 100);
  const binCount = 28;
  const low = simulations[0];
  const high = simulations[simulations.length - 1];
  const width = Math.max(0.001, (high - low) / binCount);
  const bins = Array.from({ length: binCount }, (_, index) => ({
    x: low + width * (index + 0.5),
    y: 0,
  }));
  simulations.forEach((value) => {
    bins[Math.min(binCount - 1, Math.floor((value - low) / width))].y += 1;
  });
  const monteCarlo = {
    animationDuration: 900,
    tooltip: {
      ...tooltip,
      trigger: "axis",
      formatter: (items: Array<{ value: number[] }>) =>
        `Caja: ${money(items[0].value[0])}<br/>Simulaciones: ${items[0].value[1]}`,
    },
    grid: { left: 50, right: 20, top: 28, bottom: 48 },
    xAxis: {
      type: "value",
      name: "Flujo mensual (millones)",
      ...axis,
      nameTextStyle: chartText,
    },
    yAxis: {
      type: "value",
      name: "Frecuencia",
      ...axis,
      nameTextStyle: chartText,
    },
    series: [
      {
        type: "bar",
        data: bins.map((bin) => [bin.x, bin.y]),
        barWidth: "92%",
        itemStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 1,
            x2: 0,
            y2: 0,
            colorStops: [
              { offset: 0, color: "rgba(139,92,246,.3)" },
              { offset: 1, color: "#22d3ee" },
            ],
          },
          borderColor: "#67e8f9",
          borderWidth: 0.5,
          shadowBlur: 10,
          shadowColor: "rgba(34,211,238,.35)",
        },
        markLine: {
          symbol: "none",
          label: { color: "#f8fafc" },
          lineStyle: {
            color: "#f4b942",
            width: 2,
            shadowBlur: 12,
            shadowColor: "#f4b942",
          },
          data: [
            { xAxis: 0, name: "Equilibrio" },
            { xAxis: percentile(0.5), name: "P50" },
          ],
        },
      },
    ],
  };
  const surface = [] as number[][];
  for (let inflation = 0; inflation <= 8; inflation += 0.5)
    for (let sales = -15; sales <= 15; sales += 1.5) {
      const r = state.revenue * (1 + sales / 100) * (1 + inflation / 100);
      const c =
        state.costs *
        (1 + inflation / 100 + (state.importShare / 100) * (state.fx / 100));
      surface.push([
        inflation,
        sales,
        r ? ((r - c - debtService) / r) * 100 : 0,
      ]);
    }
  const sensitivity = {
    animationDuration: reducedMotion ? 0 : 900,
    tooltip: {
      trigger: "item",
      backgroundColor: "rgba(5,7,13,.96)",
      borderColor: "rgba(34,211,238,.45)",
      textStyle: { color: "#f8fafc" },
      formatter: (item: { value: number[]; seriesName: string }) =>
        item.seriesName === "Escenario actual"
          ? `ESCENARIO ACTUAL<br/>Inflación: ${item.value[0]}%<br/>Ventas: ${item.value[1]}%<br/>Margen: ${item.value[2].toFixed(1)}%`
          : `Inflación: ${item.value[0]}%<br/>Ventas: ${item.value[1]}%<br/>Margen: ${item.value[2].toFixed(1)}%`,
    },
    grid: { left: 58, right: 38, top: 26, bottom: 72 },
    xAxis: {
      type: "value",
      name: "Inflación mensual %",
      min: 0,
      max: 8,
      ...axis,
      nameTextStyle: chartText,
    },
    yAxis: {
      type: "value",
      name: "Variación de ventas %",
      min: -15,
      max: 15,
      ...axis,
      nameTextStyle: chartText,
    },
    visualMap: {
      min: -30,
      max: 35,
      calculable: true,
      orient: "horizontal",
      left: "center",
      bottom: 10,
      text: ["Margen alto", "Margen bajo"],
      textStyle: chartText,
      inRange: { color: ["#ff275f", "#7c3aed", "#00b8ff", "#39ffb6"] },
    },
    series: [
      {
        name: "Superficie de margen",
        type: "heatmap",
        data: surface,
        progressive: 400,
        itemStyle: {
          borderColor: "rgba(5,7,13,.34)",
          borderWidth: 1,
          shadowBlur: 8,
          shadowColor: "rgba(34,211,238,.22)",
        },
        emphasis: {
          itemStyle: {
            borderColor: "#fff",
            borderWidth: 2,
            shadowBlur: 22,
            shadowColor: "#22d3ee",
          },
        },
      },
      {
        name: "Escenario actual",
        type: "effectScatter",
        data: [[state.inflation, state.sales, margin]],
        symbolSize: 18,
        rippleEffect: { scale: 4, brushType: "stroke" },
        itemStyle: {
          color: "#fff",
          borderColor: "#f4b942",
          borderWidth: 4,
          shadowBlur: 28,
          shadowColor: "#f4b942",
        },
        z: 10,
      },
    ],
  };
  const radar = {
    animationDuration: 800,
    tooltip,
    radar: {
      indicator: [
        { name: "Caja", max: 100 },
        { name: "Margen", max: 100 },
        { name: "Deuda", max: 100 },
        { name: "Ventas", max: 100 },
        { name: "Costos", max: 100 },
      ],
      splitNumber: 4,
      axisName: { color: "#cbd5e1" },
      splitLine: { lineStyle: { color: "rgba(34,211,238,.16)" } },
      splitArea: {
        areaStyle: { color: ["rgba(34,211,238,.02)", "rgba(139,92,246,.035)"] },
      },
      axisLine: { lineStyle: { color: "rgba(34,211,238,.2)" } },
    },
    series: [
      {
        type: "radar",
        data: [
          {
            value: [
              stress,
              Math.max(0, Math.min(100, margin * 4)),
              Math.max(0, 100 - state.rate),
              Math.max(0, 60 + state.sales * 5),
              Math.max(0, 100 - state.inflation * 10),
            ],
            name: "Resiliencia",
          },
        ],
        symbolSize: 7,
        lineStyle: neonLine(tone),
        itemStyle: { color: tone },
        areaStyle: { color: `${tone}35` },
      },
    ],
  };
  return (
    <div className="lab-grid">
      <section className="lab-console panel">
        <div className="panel-title">
          <div>
            <span>ESCENARIO INTERACTIVO · NO ES PRONÓSTICO</span>
            <h2>
              <Gamepad2 />
              Mové la economía y probá tu negocio
            </h2>
          </div>
        </div>
        <div className="preset-row">
          <button
            onClick={() =>
              setState((s) => ({
                ...s,
                inflation: live("ipc", 2.1),
                sales: live("sales", -3.8),
                fx: 2,
                rate: live("rate", 35),
              }))
            }
          >
            Base actual
          </button>
          <button
            onClick={() =>
              setState((s) => ({
                ...s,
                inflation: 1.5,
                sales: 2,
                fx: 1,
                rate: 25,
              }))
            }
          >
            Recuperación
          </button>
          <button
            onClick={() =>
              setState((s) => ({
                ...s,
                inflation: 4.5,
                sales: -8,
                fx: 9,
                rate: 55,
              }))
            }
          >
            Estrés
          </button>
        </div>
        <div className="slider-grid">
          <Slider
            label="Ventas mensuales"
            value={state.revenue}
            min={1}
            max={100}
            suffix=" M"
            onChange={(v) => set("revenue", v)}
          />
          <Slider
            label="Costos mensuales"
            value={state.costs}
            min={1}
            max={100}
            suffix=" M"
            onChange={(v) => set("costs", v)}
          />
          <Slider
            label="Deuda total"
            value={state.debt}
            min={0}
            max={100}
            suffix=" M"
            onChange={(v) => set("debt", v)}
          />
          <Slider
            label="Tasa anual"
            value={state.rate}
            min={0}
            max={120}
            onChange={(v) => set("rate", v)}
          />
          <Slider
            label="Inflación mensual"
            value={state.inflation}
            min={0}
            max={12}
            step={0.1}
            onChange={(v) => set("inflation", v)}
          />
          <Slider
            label="Variación dólar"
            value={state.fx}
            min={-5}
            max={25}
            step={0.5}
            onChange={(v) => set("fx", v)}
          />
          <Slider
            label="Variación volumen vendido"
            value={state.sales}
            min={-20}
            max={20}
            step={0.5}
            onChange={(v) => set("sales", v)}
          />
          <Slider
            label="Costos importados"
            value={state.importShare}
            min={0}
            max={100}
            onChange={(v) => set("importShare", v)}
          />
        </div>
      </section>
      <section
        className="lab-score panel"
        style={{ "--score-tone": tone } as React.CSSProperties}
      >
        <div className="score-orbit">
          <div>
            <small>RESISTENCIA DEL ESCENARIO</small>
            <motion.strong
              key={Math.round(stress)}
              initial={{ scale: 0.85 }}
              animate={{ scale: 1 }}
            >
              {Math.round(stress)}
            </motion.strong>
            <b>/100</b>
          </div>
        </div>
        <span className="scenario-label">ESCENARIO {scenario}</span>
        <ReactECharts option={radar} style={{ height: 285 }} />
        <ChartContract
          x="Radar sin eje temporal: cada radio es una dimensión del escenario cargado por el usuario."
          y="Escala normalizada 0–100; 100 significa mayor resistencia dentro de esta regla heurística."
          encoding="Polígono cian: margen, demanda, costo financiero, poder de compra y cobertura bajo los supuestos elegidos."
          period="Escenario interactivo; no representa un período oficial."
          sources="Datos ingresados por el usuario + fórmulas visibles del laboratorio."
          caution="No es una probabilidad ni un índice oficial. Sólo sirve para comparar escenarios construidos con la misma fórmula."
        />
        <div className="score-explainer">
          <h3>¿Qué significa este puntaje?</h3>
          <p>
            Resume cuánto margen tendría el negocio para absorber inflación,
            caída de ventas y costo financiero bajo{" "}
            <b>los valores que usted cargó</b>.
          </p>
          <code>50 + 2×margen − 2×caída de ventas − tasa anual÷8</code>
          <ul>
            <li>
              <b>70–100:</b> mayor resistencia.
            </li>
            <li>
              <b>40–69:</b> conviene vigilar caja.
            </li>
            <li>
              <b>0–39:</b> escenario de tensión.
            </li>
          </ul>
          <small>
            Es un indicador heurístico del simulador, no un índice oficial ni
            una probabilidad de supervivencia.
          </small>
        </div>
      </section>
      <section className="outcomes panel panel-wide">
        <div className="outcome-card">
          <Building2 />
          <span>Flujo mensual simulado</span>
          <motion.b key={cash.toFixed(2)}>{money(cash)}</motion.b>
          <small>Ventas ajustadas − costos − interés mensual</small>
        </div>
        <div className="outcome-card">
          <Gauge />
          <span>Margen después de deuda</span>
          <motion.b key={margin.toFixed(1)}>{margin.toFixed(1)}%</motion.b>
          <small>Escenario, no dato contable</small>
        </div>
        <div className="outcome-card">
          <WalletCards />
          <span>Costo financiero mensual</span>
          <motion.b key={debtService.toFixed(2)}>{money(debtService)}</motion.b>
          <small>Tasa efectiva aproximada</small>
        </div>
        <div className="outcome-card">
          <Users />
          <span>Poder de compra</span>
          <motion.b key={purchasingPower.toFixed(1)}>
            {purchasingPower > 0 ? "+" : ""}
            {purchasingPower.toFixed(1)}%
          </motion.b>
          <small>Ingreso − inflación del escenario</small>
        </div>
        <div className="outcome-card">
          <ShieldCheck />
          <span>Cobertura de intereses</span>
          <motion.b key={interestCoverage.toFixed(2)}>
            {Number.isFinite(interestCoverage)
              ? `${interestCoverage.toFixed(2)}×`
              : "Sin deuda"}
          </motion.b>
          <small>Resultado operativo ÷ interés estimado</small>
        </div>
        <div className="outcome-card">
          <Activity />
          <span>Punto de equilibrio</span>
          <motion.b key={breakEvenRevenue.toFixed(2)}>
            {money(breakEvenRevenue)}
          </motion.b>
          <small>Ingreso mínimo para cubrir costos e interés</small>
        </div>
        <div className="outcome-card">
          <Gauge />
          <span>Margen de seguridad</span>
          <motion.b key={safetyMargin.toFixed(1)}>
            {safetyMargin.toFixed(1)}%
          </motion.b>
          <small>Distancia porcentual al equilibrio</small>
        </div>
        <div className="outcome-card">
          <DollarSign />
          <span>Exposición cambiaria</span>
          <motion.b key={fxExposure.toFixed(2)}>{money(fxExposure)}</motion.b>
          <small>Impacto directo del dólar sobre costos importados</small>
        </div>
        <div className="income-control">
          <Slider
            label="Aumento mensual del ingreso familiar"
            value={state.incomeRise}
            min={-5}
            max={15}
            step={0.5}
            onChange={(v) => set("incomeRise", v)}
          />
        </div>
        <p className="formula-note">
          <ShieldCheck />
          Motor transparente: no adivina el futuro. Cruza tus supuestos con
          relaciones aritméticas explícitas. La cuota muestra interés mensual
          aproximado y no reemplaza el sistema de amortización de tu préstamo.
        </p>
      </section>
      <Panel
        title="2.000 futuros posibles de caja"
        kicker="MONTE CARLO · SEMILLA REPRODUCIBLE"
        icon={<Sparkles />}
      >
        <ReactECharts option={monteCarlo} style={{ height: 330 }} />
        <ChartContract
          x="Resultado mensual de caja simulado, en la misma moneda de los importes cargados."
          y="Cantidad de trayectorias dentro de cada intervalo del histograma."
          encoding="Distribución de 2.000 simulaciones; P10/P50/P90 y Expected Shortfall resumen centro y cola adversa."
          period="Un mes hipotético bajo el escenario del laboratorio."
          sources="Supuestos del usuario + simulador reproducible con semilla fija."
          caution="Las dispersiones son supuestos declarados, no volatilidades estimadas de una empresa real. No es pronóstico oficial."
        />
        <div className="probability-strip">
          <span>
            P10 <b>{money(percentile(0.1))}</b>
          </span>
          <span>
            Expected Shortfall 10% <b>{money(expectedShortfall10)}</b>
          </span>
          <span>
            Valor esperado <b>{money(expectedCash)}</b>
          </span>
          <span>
            P50 <b>{money(percentile(0.5))}</b>
          </span>
          <span>
            P90 <b>{money(percentile(0.9))}</b>
          </span>
          <span>
            Prob. caja positiva <b>{positiveProbability.toFixed(1)}%</b>
          </span>
          <span>
            Asimetría favorable/adversa <b>{asymmetry.toFixed(2)}×</b>
          </span>
        </div>
        <p className="chart-disclaimer">
          Escenario probabilístico: dispersiones asumidas de inflación ±0,75 pp,
          dólar ±3,2 pp, ventas ±4 pp y tasa ±8 pp. No son intervalos oficiales.
        </p>
      </Panel>
      <Panel
        title="Mapa de margen: explorá cada combinación"
        kicker="MATRIZ DE SENSIBILIDAD · PULSO ACTUAL"
        icon={<Activity />}
      >
        <ReactECharts option={sensitivity} style={{ height: 410 }} />
        <ChartContract
          x="Variación del volumen vendido (%) definida por el usuario."
          y="Inflación mensual (%) definida por el usuario."
          encoding="Color de cada celda: margen simulado; el pulso marca la combinación seleccionada."
          period="Escenario estático de sensibilidad, sin fecha oficial."
          sources="Ingresos, costos, deuda y supuestos ingresados en el laboratorio."
          caution="Ceteris paribus: cambia inflación y ventas manteniendo los demás controles fijos. No predice causalidad."
        />
        <p className="chart-disclaimer">
          Cada celda cruza inflación y volumen de ventas manteniendo los demás
          controles del escenario. El pulso marca tus supuestos actuales. Es
          sensibilidad matemática, no predicción.
        </p>
      </Panel>
    </div>
  );
}

function App() {
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(tabs[0]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<
    Array<{ type: string; title: string; subtitle: string; url: string }>
  >([]);
  const [frame, setFrame] = useState(0);
  const [projectionFrame, setProjectionFrame] = useState(0);
  const [macroFrame, setMacroFrame] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [clock, setClock] = useState(new Date());
  const haloRef = useRef<HTMLDivElement>(null);

  async function load(force = false) {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `/api/dashboard${force ? "?refresh=1" : ""}`,
      );
      if (!response.ok) throw new Error(`Error ${response.status}`);
      setData(await response.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo actualizar");
      setData((current) => current ?? fallbackDashboard);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const timer = setInterval(() => load(), 300000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    let frameId = 0;
    const move = (event: PointerEvent) => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        if (haloRef.current)
          haloRef.current.style.transform = `translate3d(${event.clientX - 120}px, ${event.clientY - 120}px, 0)`;
      });
    };
    window.addEventListener("pointermove", move, { passive: true });
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("pointermove", move);
    };
  }, []);
  useEffect(() => {
    if (!search.trim()) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      const response = await fetch(
        `/api/search?q=${encodeURIComponent(search)}`,
      );
      if (response.ok) {
        const payload = (await response.json()) as {
          results: Array<{
            type: string;
            title: string;
            subtitle: string;
            url: string;
          }>;
        };
        setResults(payload.results);
      }
    }, 220);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    if (!playing || reducedMotion || !data || data.consumption.length === 0)
      return;
    const timer = setInterval(
      () => setFrame((value) => (value + 1) % data.consumption.length),
      1300,
    );
    return () => clearInterval(timer);
  }, [playing, reducedMotion, data]);
  useEffect(() => {
    if (!playing || reducedMotion || !data) return;
    const available = data.metrics.find((item) => item.id === "exports")?.history?.filter((point) => point.date >= "2025-01").length ?? 0;
    if (!available) return;
    const timer = setInterval(() => setMacroFrame((value) => (value + 1) % available), 1350);
    return () => clearInterval(timer);
  }, [playing, reducedMotion, data]);

  const metric = (id: string) => data?.metrics.find((item) => item.id === id);
  const comparisonOption = useMemo(() => {
    if (!data) return {};
    const ipc = metric("ipc")?.history ?? [];
    const ipim = metric("ipim")?.history ?? [];
    const labels = [
      ...new Set([
        ...ipc.map((p) => p.date.slice(0, 7)),
        ...ipim.map((p) => p.date.slice(0, 7)),
      ]),
    ].sort();
    return {
      animationDuration: 700,
      tooltip,
      legend: { top: 0, textStyle: chartText },
      grid: { left: 46, right: 20, top: 42, bottom: 40 },
      xAxis: { type: "category", data: labels, ...axis },
      yAxis: {
        type: "value",
        ...axis,
        axisLabel: { ...chartText, formatter: "{value}%" },
      },
      series: [
        {
          name: "IPC",
          type: "line",
          smooth: true,
          data: labels.map(
            (label) => ipc.find((p) => p.date.startsWith(label))?.value ?? null,
          ),
          lineStyle: neonLine("#22d3ee"),
          itemStyle: {
            color: "#e6fbff",
            borderColor: "#22d3ee",
            borderWidth: 3,
            shadowBlur: 20,
            shadowColor: "#22d3ee",
          },
          areaStyle: { color: "rgba(34,211,238,.10)" },
          tooltip: { valueFormatter: (value: number) => `${Number(value).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}% mensual` },
        },
        {
          name: "IPIM",
          type: "line",
          smooth: true,
          data: labels.map(
            (label) =>
              ipim.find((p) => p.date.startsWith(label))?.value ?? null,
          ),
          lineStyle: neonLine("#ff3f75"),
          itemStyle: {
            color: "#fff",
            borderColor: "#ff3f75",
            borderWidth: 3,
            shadowBlur: 20,
            shadowColor: "#ff3f75",
          },
          tooltip: { valueFormatter: (value: number) => `${Number(value).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}% mensual` },
        },
      ],
    };
  }, [data]);

  const regionalOption = useMemo(
    () =>
      !data
        ? {}
        : {
            animationDuration: 650,
            tooltip,
            legend: { top: 0, textStyle: chartText },
            grid: { left: 78, right: 20, top: 40, bottom: 34 },
            xAxis: {
              type: "value",
              ...axis,
              axisLabel: { ...chartText, formatter: "{value}%" },
            },
            yAxis: {
              type: "category",
              data: data.regional.map((r) => r.region),
              ...axis,
            },
            series: [
              {
                name: "IPC",
                type: "bar",
                data: data.regional.map((r) => r.ipc),
                itemStyle: { color: "#3b82f6", borderRadius: 6 },
                tooltip: { valueFormatter: (value: number) => `${Number(value).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}% mensual` },
              },
              {
                name: "Vivienda/Energía",
                type: "bar",
                data: data.regional.map((r) => r.vivienda),
                itemStyle: { color: "#f4b942", borderRadius: 6 },
                tooltip: { valueFormatter: (value: number) => `${Number(value).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}% mensual` },
              },
            ],
          },
    [data],
  );

  const motionOption = useMemo(() => {
    if (!data) return {};
    const current = data.consumption[frame] ?? data.consumption[0];
    const trail = data.consumption
      .slice(0, frame + 1)
      .map((point) => [point.ipc, point.sales]);
    const [month, year] = current.date.split(" ");
    return {
      animationDurationUpdate: reducedMotion ? 0 : 800,
      tooltip: {
        ...tooltip,
        trigger: "item",
        formatter: () =>
          `${current.date}<br/>IPC nacional mensual: ${current.ipc}%<br/>Ventas minoristas PyME reales: ${current.sales}% i.a.<br/>Estado: DATO OFICIAL`,
      },
      grid: { left: 76, right: 40, top: 38, bottom: 70 },
      xAxis: {
        name: "Inflación mensual (%) →",
        nameLocation: "middle",
        nameGap: 42,
        min: 1,
        max: 4,
        ...axis,
        nameTextStyle: chartText,
      },
      yAxis: {
        name: "Ventas PyME vs. mismo mes de 2025 (%) →",
        nameLocation: "middle",
        nameGap: 52,
        min: -7,
        max: 2,
        ...axis,
        nameTextStyle: chartText,
      },
      series: [
        {
          name: "Trayectoria",
          type: "line",
          data: trail,
          symbol: "none",
          lineStyle: neonLine("#22d3ee", true),
          z: 1,
        },
        {
          name: "Mes observado",
          type: "effectScatter",
          symbolSize: 76,
          rippleEffect: { scale: 4, brushType: "stroke" },
          data: [[current.ipc, current.sales]],
          itemStyle: {
            color: {
              type: "radial",
              x: 0.35,
              y: 0.3,
              r: 0.8,
              colorStops: [
                { offset: 0, color: "#dffcff" },
                { offset: 0.5, color: "#22d3ee" },
                { offset: 1, color: "#7c3aed" },
              ],
            },
            shadowBlur: 34,
            shadowColor: "rgba(34,211,238,.7)",
          },
          label: {
            show: true,
            formatter: `${month.toUpperCase()}\n${year}`,
            color: "#fff",
            fontWeight: 900,
            fontSize: 16,
            lineHeight: 19,
            textBorderColor: "#05070d",
            textBorderWidth: 4,
          },
        },
      ],
    };
  }, [data, frame, reducedMotion]);

  const priceMotionOption = useMemo(() => {
    if (!data) return {};
    const ipc =
      metric("ipc")?.history?.filter((point) =>
        point.date.startsWith("2026"),
      ) ?? [];
    const ipim = metric("ipim")?.history ?? [];
    const joined = ipim.flatMap((point) => {
      const consumer = ipc.find(
        (item) => item.date.slice(0, 7) === point.date.slice(0, 7),
      );
      return consumer
        ? [
            {
              date: point.date.slice(0, 7),
              ipc: consumer.value,
              ipim: point.value,
            },
          ]
        : [];
    });
    const index = Math.min(frame, Math.max(0, joined.length - 1)),
      current = joined[index];
    if (!current) return {};
    const trail = joined
      .slice(0, index + 1)
      .map((point) => [point.ipc, point.ipim]);
    return {
      animationDurationUpdate: reducedMotion ? 0 : 800,
      tooltip: {
        ...tooltip,
        trigger: "item",
        formatter: () =>
          `${current.date}<br/>IPC: ${current.ipc}% mensual<br/>IPIM: ${current.ipim}% mensual<br/>Ambos: INDEC`,
      },
      grid: { left: 76, right: 40, top: 38, bottom: 70 },
      xAxis: {
        name: "Inflación al consumidor, mensual (%) →",
        nameLocation: "middle",
        nameGap: 42,
        min: 0,
        max: 6,
        ...axis,
        nameTextStyle: chartText,
      },
      yAxis: {
        name: "Precios mayoristas, mensual (%) →",
        nameLocation: "middle",
        nameGap: 52,
        min: 0,
        max: 6,
        ...axis,
        nameTextStyle: chartText,
      },
      series: [
        {
          type: "line",
          data: trail,
          symbol: "none",
          lineStyle: neonLine("#ff3f75", true),
        },
        {
          type: "effectScatter",
          symbolSize: 74,
          rippleEffect: { scale: 4 },
          data: [[current.ipc, current.ipim]],
          itemStyle: {
            color: {
              type: "radial",
              x: 0.35,
              y: 0.3,
              r: 0.8,
              colorStops: [
                { offset: 0, color: "#fff" },
                { offset: 0.48, color: "#ff3f75" },
                { offset: 1, color: "#f4b942" },
              ],
            },
            shadowBlur: 34,
            shadowColor: "#ff3f75",
          },
          label: {
            show: true,
            formatter: current.date.replace("-", "\n"),
            color: "#fff",
            fontWeight: 900,
            fontSize: 15,
            lineHeight: 18,
            textBorderColor: "#05070d",
            textBorderWidth: 4,
          },
        },
      ],
    };
  }, [data, frame, reducedMotion]);

  const ipcProjectionMotion = useMemo(() => {
    const history =
      data?.metrics.find((item) => item.id === "ipc")?.history ?? [];
    try {
      const result = buildForecast(history, 5, "monthly");
      return [
        ...history
          .filter((point) => point.date.startsWith("2026"))
          .map((point) => ({ ...point, status: "REAL" as const, width95: 0 })),
        ...result.forecast.map((point) => ({
          ...point,
          status: "MODELO" as const,
          width95: point.upper95 - point.lower95,
        })),
      ];
    } catch {
      return [];
    }
  }, [data]);
  useEffect(() => {
    if (!playing || reducedMotion || ipcProjectionMotion.length === 0) return;
    const timer = setInterval(
      () =>
        setProjectionFrame((value) => (value + 1) % ipcProjectionMotion.length),
      1300,
    );
    return () => clearInterval(timer);
  }, [playing, reducedMotion, ipcProjectionMotion.length]);
  const ipcProjectionMotionOption = useMemo(() => {
    const current =
      ipcProjectionMotion[projectionFrame] ?? ipcProjectionMotion[0];
    if (!current) return {};
    const index = ipcProjectionMotion.indexOf(current),
      trail = ipcProjectionMotion
        .slice(0, index + 1)
        .map((point, pointIndex) => [pointIndex + 1, point.value]);
    const real = current.status === "REAL",
      color = real ? "#22d3ee" : "#f4b942";
    return {
      animationDurationUpdate: reducedMotion ? 0 : 850,
      tooltip: {
        ...tooltip,
        trigger: "item",
        formatter: () =>
          `${current.date}<br/>IPC mensual: ${current.value.toFixed(2)}%<br/>Clase: ${current.status}${real ? "" : `<br/>Ancho banda 95%: ${current.width95.toFixed(2)} pp`}`,
      },
      grid: { left: 76, right: 40, top: 38, bottom: 70 },
      xAxis: {
        type: "value",
        name: "Mes de 2026 (1=enero · 12=diciembre) →",
        nameLocation: "middle",
        nameGap: 42,
        min: 1,
        max: 12,
        interval: 1,
        ...axis,
        nameTextStyle: chartText,
      },
      yAxis: {
        type: "value",
        name: "Inflación mensual (%) →",
        nameLocation: "middle",
        nameGap: 48,
        min: 0,
        ...axis,
        nameTextStyle: chartText,
      },
      series: [
        {
          type: "line",
          data: trail,
          symbol: "none",
          lineStyle: neonLine(color, !real),
        },
        {
          type: "effectScatter",
          symbolSize: real ? 70 : Math.min(88, 64 + current.width95 * 5),
          rippleEffect: { scale: 4 },
          data: [[index + 1, current.value]],
          itemStyle: {
            color,
            borderColor: "#fff",
            borderWidth: 2,
            shadowBlur: 34,
            shadowColor: color,
          },
          label: {
            show: true,
            formatter: `${current.date.slice(5)}\n${current.status}`,
            color: "#fff",
            fontWeight: 900,
            fontSize: 14,
            lineHeight: 17,
            textBorderColor: "#05070d",
            textBorderWidth: 4,
          },
        },
      ],
    };
  }, [ipcProjectionMotion, projectionFrame, reducedMotion]);

  const boardPulseOption = useMemo(() => {
    if (!data) return {};
    const months = Array.from(
      { length: 12 },
      (_, index) => `2026-${String(index + 1).padStart(2, "0")}`,
    );
    const labels = [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ];
    const reveal = Math.min(11, projectionFrame);
    const ipcPoints = ipcProjectionMotion;
    const ipcReal = months.map((month, index) =>
      index <= reveal
        ? (ipcPoints.find(
            (point) =>
              point.date.slice(0, 7) === month && point.status === "REAL",
          )?.value ?? null)
        : null,
    );
    const lastRealIndex = ipcPoints.reduce(
      (last, point, index) => (point.status === "REAL" ? index : last),
      -1,
    );
    const lastReal = ipcPoints[lastRealIndex];
    const ipcModel = months.map((month, index) => {
      if (index > reveal) return null;
      if (lastReal?.date.slice(0, 7) === month) return lastReal.value;
      return (
        ipcPoints.find(
          (point) =>
            point.date.slice(0, 7) === month && point.status === "MODELO",
        )?.value ?? null
      );
    });
    const salesHistory = metric("sales")?.history ?? [];
    const sales = months.map((month, index) =>
      index <= reveal
        ? (salesHistory.find((point) => point.date.slice(0, 7) === month)
            ?.value ?? null)
        : null,
    );
    const rateMetric = metric("rate");
    const observedRate = rateMetric?.history
      ? rateMetric.frequency === "DIARIA"
        ? monthlyAverage(rateMetric.history)
        : rateMetric.history
      : [];
    const rates = months.map((month, index) =>
      index <= reveal
        ? (observedRate.find((point) => point.date.slice(0, 7) === month)
            ?.value ?? null)
        : null,
    );
    const expectedRates = months.map((month, index) => {
      if (index > reveal) return null;
      if (month === "2026-07")
        return (
          metric("rate_rem_aug")?.value ??
          (rateMetric?.status === "EXPECTATIVA" ? rateMetric.value : null)
        );
      if (month === "2026-12") return metric("rate_rem_dec")?.value ?? null;
      return null;
    });
    const pulse = (gridIndex: number, value: number | null, color: string) =>
      value == null
        ? null
        : {
            type: "effectScatter",
            xAxisIndex: gridIndex,
            yAxisIndex: gridIndex,
            data: [[labels[reveal], value]],
            symbolSize: 17,
            rippleEffect: { scale: 3.4, brushType: "stroke" },
            itemStyle: {
              color: "#fff",
              borderColor: color,
              borderWidth: 4,
              shadowBlur: 24,
              shadowColor: color,
            },
            tooltip: { show: false },
            z: 12,
          };
    const currentIpc = (ipcModel[reveal] ?? ipcReal[reveal]) as number | null;
    const currentSales = sales[reveal] as number | null;
    const currentRate = (rates[reveal] ?? expectedRates[reveal]) as
      | number
      | null;
    return {
      animationDurationUpdate: reducedMotion ? 0 : 700,
      tooltip: {
        ...tooltip,
        trigger: "axis",
        axisPointer: {
          type: "line",
          lineStyle: { color: "rgba(255,255,255,.28)" },
        },
      },
      legend: {
        top: 0,
        right: 12,
        textStyle: chartText,
        data: [
          "IPC real",
          "IPC modelo",
          "Ventas PyME reales",
          "TAMAR real",
          "TAMAR REM",
        ],
      },
      title: [
        {
          text: "PRECIOS",
          subtext: "Inflación mensual · %",
          left: 8,
          top: 40,
          textStyle: { color: "#67e8f9", fontSize: 11 },
          subtextStyle: { color: "#94a3b8", fontSize: 10 },
        },
        {
          text: "CONSUMO",
          subtext: "Ventas PyME vs. igual mes de 2025 · %",
          left: 8,
          top: 200,
          textStyle: { color: "#fda4af", fontSize: 11 },
          subtextStyle: { color: "#94a3b8", fontSize: 10 },
        },
        {
          text: "COSTO DE LA DEUDA",
          subtext: "TAMAR · tasa nominal anual %",
          left: 8,
          top: 360,
          textStyle: { color: "#fde68a", fontSize: 11 },
          subtextStyle: { color: "#94a3b8", fontSize: 10 },
        },
      ],
      grid: [
        { left: 150, right: 28, top: 64, height: 105 },
        { left: 150, right: 28, top: 224, height: 105 },
        { left: 150, right: 28, top: 384, height: 105 },
      ],
      xAxis: [0, 1, 2].map((index) => ({
        type: "category",
        gridIndex: index,
        data: labels,
        boundaryGap: false,
        ...axis,
        axisLabel: { ...chartText, show: index === 2 },
      })),
      yAxis: [
        {
          type: "value",
          gridIndex: 0,
          name: "% mensual",
          ...axis,
          nameTextStyle: chartText,
          axisLabel: { ...chartText, formatter: "{value}%" },
        },
        {
          type: "value",
          gridIndex: 1,
          name: "% interanual",
          ...axis,
          nameTextStyle: chartText,
          axisLabel: { ...chartText, formatter: "{value}%" },
        },
        {
          type: "value",
          gridIndex: 2,
          name: "% TNA",
          ...axis,
          nameTextStyle: chartText,
          axisLabel: { ...chartText, formatter: "{value}%" },
        },
      ],
      series: [
        {
          name: "IPC real",
          type: "line",
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: ipcReal,
          connectNulls: false,
          symbolSize: 7,
          lineStyle: neonLine("#22d3ee"),
          itemStyle: { color: "#fff", borderColor: "#22d3ee", borderWidth: 2 },
          tooltip: { valueFormatter: (value: number) => `${Number(value).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%` },
        },
        {
          name: "IPC modelo",
          type: "line",
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: ipcModel,
          connectNulls: false,
          symbolSize: 7,
          lineStyle: neonLine("#f4b942", true),
          itemStyle: { color: "#fff", borderColor: "#f4b942", borderWidth: 2 },
          tooltip: { valueFormatter: (value: number) => `${Number(value).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}% · MODELO` },
        },
        {
          name: "Ventas PyME reales",
          type: "line",
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: sales,
          connectNulls: false,
          symbolSize: 7,
          lineStyle: neonLine("#ff3f75"),
          itemStyle: { color: "#fff", borderColor: "#ff3f75", borderWidth: 2 },
          tooltip: { valueFormatter: (value: number) => `${Number(value).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}% interanual` },
          markLine: {
            symbol: "none",
            silent: true,
            data: [{ yAxis: 0 }],
            lineStyle: { color: "rgba(255,255,255,.24)", type: "dashed" },
            label: { show: false },
          },
        },
        {
          name: "TAMAR real",
          type: "line",
          xAxisIndex: 2,
          yAxisIndex: 2,
          data: rates,
          connectNulls: false,
          symbolSize: 7,
          lineStyle: neonLine("#39ffb6"),
          itemStyle: { color: "#fff", borderColor: "#39ffb6", borderWidth: 2 },
          tooltip: { valueFormatter: (value: number) => `${Number(value).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}% TNA` },
        },
        {
          name: "TAMAR REM",
          type: "scatter",
          xAxisIndex: 2,
          yAxisIndex: 2,
          data: expectedRates,
          symbol: "diamond",
          symbolSize: 11,
          itemStyle: { color: "#f4b942", borderColor: "#fff", borderWidth: 1 },
          tooltip: {
            valueFormatter: (value: number) => `${value}% TNA · EXPECTATIVA`,
          },
        },
        pulse(
          0,
          currentIpc,
          currentIpc === ipcModel[reveal] ? "#f4b942" : "#22d3ee",
        ),
        pulse(1, currentSales, "#ff3f75"),
        pulse(2, currentRate, "#39ffb6"),
      ].filter(Boolean),
    };
  }, [data, ipcProjectionMotion, projectionFrame, reducedMotion]);

  const sevenSignalsOption = useMemo(() => {
    if (!data) return {};
    const months = Array.from(
      { length: 12 },
      (_, index) => `2026-${String(index + 1).padStart(2, "0")}`,
    );
    const labels = [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ];
    const reveal = Math.min(11, projectionFrame);
    const byMetric = (id: string) => {
      const item = metric(id);
      const history = item?.history
        ? item.frequency === "DIARIA"
          ? monthlyAverage(item.history)
          : item.history
        : [];
      return months.map((month, index) =>
        index <= reveal
          ? (history.find((point) => point.date.slice(0, 7) === month)?.value ??
            null)
          : null,
      );
    };
    const ipcReal = months.map((month, index) =>
      index <= reveal
        ? (ipcProjectionMotion.find(
            (point) =>
              point.date.slice(0, 7) === month && point.status === "REAL",
          )?.value ?? null)
        : null,
    );
    const ipcModel = months.map((month, index) =>
      index <= reveal
        ? (ipcProjectionMotion.find(
            (point) =>
              point.date.slice(0, 7) === month && point.status === "MODELO",
          )?.value ?? null)
        : null,
    );
    const lanes = [
      {
        id: "ipc",
        title: "1 · IPC",
        unit: "% mensual",
        color: "#22d3ee",
        values: ipcReal,
        model: ipcModel,
        note: "INDEC · modelo sólo ago–dic",
      },
      {
        id: "real_wage",
        title: "2 · SALARIO REAL",
        unit: "% mensual",
        color: "#8b5cf6",
        values: byMetric("real_wage"),
        note: "Calculado: salarios INDEC descontando IPC",
      },
      {
        id: "debt_income",
        title: "3 · DEUDA / INGRESO",
        unit: "% ingreso",
        color: "#64748b",
        values: byMetric("debt_income"),
        note: "SIN SERIE MENSUAL OFICIAL HOMOGÉNEA",
      },
      {
        id: "delinquency",
        title: "4 · MOROSIDAD FAMILIAS",
        unit: "% cartera",
        color: "#ff3f75",
        values: byMetric("delinquency"),
        note: "BCRA · financiaciones irregulares",
      },
      {
        id: "sales",
        title: "5 · CONSUMO PYME",
        unit: "% interanual",
        color: "#f97316",
        values: byMetric("sales"),
        note: "CAME · precios constantes",
      },
      {
        id: "activity",
        title: "6 · ACTIVIDAD EMAE",
        unit: "% mensual s.e.",
        color: "#39ffb6",
        values: byMetric("activity"),
        note: "INDEC · serie desestacionalizada",
      },
      {
        id: "investment",
        title: "7 · INVERSIÓN FBCF",
        unit: "% interanual",
        color: "#f4b942",
        values: byMetric("investment"),
        note: "INDEC · trimestral; punto en marzo",
      },
    ];
    const gridTop = (index: number) => 54 + index * 108;
    const series = lanes.flatMap((lane, index) => {
      const base = {
        name: lane.title,
        type: "line",
        xAxisIndex: index,
        yAxisIndex: index,
        data: lane.values,
        connectNulls: false,
        symbolSize: 6,
        lineStyle: neonLine(lane.color),
        itemStyle: { color: "#fff", borderColor: lane.color, borderWidth: 2 },
        tooltip: { valueFormatter: (value: number) => `${Number(value).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })} ${lane.unit}` },
      };
      const model = lane.model
        ? {
            name: "IPC modelo",
            type: "line",
            xAxisIndex: index,
            yAxisIndex: index,
            data: lane.model,
            connectNulls: false,
            symbolSize: 6,
            lineStyle: neonLine("#f4b942", true),
            itemStyle: {
              color: "#fff",
              borderColor: "#f4b942",
              borderWidth: 2,
            },
            tooltip: { valueFormatter: (value: number) => `${Number(value).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}% · MODELO` },
          }
        : null;
      return model ? [base, model] : [base];
    });
    return {
      aria: { enabled: true, decal: { show: true }, description: "Siete indicadores económicos en carriles separados y sincronizados por mes." },
      animationDurationUpdate: reducedMotion ? 0 : 720,
      tooltip: {
        ...tooltip,
        trigger: "axis",
        axisPointer: {
          type: "line",
          link: [{ xAxisIndex: "all" }],
          lineStyle: { color: "rgba(255,255,255,.35)" },
        },
      },
      legend: { top: 0, right: 10, textStyle: chartText, data: ["IPC modelo"] },
      title: lanes.map((lane, index) => ({
        text: lane.title,
        subtext: `${lane.unit} · ${lane.note}`,
        left: 8,
        top: gridTop(index) - 22,
        textStyle: { color: lane.color, fontSize: 11, fontWeight: 800 },
        subtextStyle: { color: "#94a3b8", fontSize: 9 },
      })),
      grid: lanes.map((_, index) => ({
        left: 190,
        right: 28,
        top: gridTop(index),
        height: 72,
      })),
      xAxis: lanes.map((_, index) => ({
        type: "category",
        gridIndex: index,
        data: labels,
        boundaryGap: false,
        ...axis,
        axisLabel: { ...chartText, show: index === lanes.length - 1 },
      })),
      yAxis: lanes.map((lane, index) => ({
        type: "value",
        gridIndex: index,
        name: lane.unit,
        nameLocation: "middle",
        nameGap: 44,
        ...axis,
        nameTextStyle: { ...chartText, fontSize: 9 },
        axisLabel: { ...chartText, fontSize: 10, formatter: "{value}%" },
      })),
      series,
      graphic: [
        {
          type: "text",
          left: 205,
          top: gridTop(2) + 25,
          style: {
            text: "Sin datos: no se reemplaza por deuda pública ni crédito/activos",
            fill: "#94a3b8",
            font: "600 11px Inter",
          },
        },
      ],
    };
  }, [data, ipcProjectionMotion, projectionFrame, reducedMotion]);

  const tradeMotionOption = useMemo(() => {
    if (!data) return {};
    const exportsHistory = (metric("exports")?.history ?? []).filter((point) => point.date >= "2025-01");
    const importsHistory = metric("imports")?.history ?? [];
    const balanceHistory = metric("trade_balance")?.history ?? [];
    const joined = exportsHistory.flatMap((exportPoint) => {
      const month = exportPoint.date.slice(0, 7);
      const importPoint = importsHistory.find((point) => point.date.slice(0, 7) === month);
      const balancePoint = balanceHistory.find((point) => point.date.slice(0, 7) === month);
      return importPoint && balancePoint ? [{ month, exports: exportPoint.value, imports: importPoint.value, balance: balancePoint.value }] : [];
    });
    const index = Math.min(macroFrame, Math.max(0, joined.length - 1));
    const current = joined[index];
    if (!current) return {};
    return {
      aria: { enabled: true, decal: { show: true }, description: "Exportaciones FOB frente a importaciones CIF; el tamaño del pulso representa la magnitud del saldo comercial." },
      animationDurationUpdate: reducedMotion ? 0 : 850,
      tooltip: { ...tooltip, trigger: "item", formatter: () => `${current.month}<br/>Exportaciones FOB: USD ${current.exports.toLocaleString("es-AR", { maximumFractionDigits: 0 })} M<br/>Importaciones CIF: USD ${current.imports.toLocaleString("es-AR", { maximumFractionDigits: 0 })} M<br/>Saldo: USD ${current.balance.toLocaleString("es-AR", { maximumFractionDigits: 0 })} M` },
      grid: { left: 92, right: 42, top: 38, bottom: 76 },
      xAxis: { type: "value", name: "Importaciones CIF del mes (millones USD) →", nameLocation: "middle", nameGap: 48, ...axis, nameTextStyle: chartText },
      yAxis: { type: "value", name: "Exportaciones FOB del mes (millones USD) →", nameLocation: "middle", nameGap: 68, ...axis, nameTextStyle: chartText },
      series: [
        { name: "Trayectoria", type: "line", data: joined.slice(0, index + 1).map((point) => [point.imports, point.exports]), symbol: "none", lineStyle: neonLine("#39ffb6", true) },
        { name: "Mes observado", type: "effectScatter", data: [[current.imports, current.exports]], symbolSize: Math.min(96, 52 + Math.sqrt(Math.abs(current.balance)) * .7), rippleEffect: { scale: 4, brushType: "stroke" }, itemStyle: { color: current.balance >= 0 ? "#39ffb6" : "#ff3f75", borderColor: "#fff", borderWidth: 2, shadowBlur: 34, shadowColor: current.balance >= 0 ? "#39ffb6" : "#ff3f75" }, label: { show: true, formatter: `${current.month}\nUSD ${Math.round(current.balance).toLocaleString("es-AR")} M`, color: "#fff", fontWeight: 900, fontSize: 13, lineHeight: 17, textBorderColor: "#05070d", textBorderWidth: 4 } }
      ]
    };
  }, [data, macroFrame, reducedMotion]);

  const monetaryTransmissionOption = useMemo(() => {
    if (!data) return {};
    const ids = ["base_money", "ipc", "activity", "real_wage"];
    const colors = ["#f4b942", "#22d3ee", "#39ffb6", "#8b5cf6"];
    const titles = ["BASE MONETARIA", "IPC", "ACTIVIDAD EMAE", "SALARIO REAL"];
    const units = ["índice ene-2025=100", "% mensual", "% mensual s.e.", "% mensual calculado"];
    const histories = ids.map((id) => {
      const item = metric(id);
      const points = item?.frequency === "DIARIA" ? monthlyAverage(item.history ?? []) : (item?.history ?? []);
      return points.filter((point) => point.date >= "2025-01");
    });
    const months = [...new Set(histories.flatMap((history) => history.map((point) => point.date.slice(0, 7))))].sort();
    const reveal = Math.min(macroFrame, Math.max(0, months.length - 1));
    const values = histories.map((history, lane) => {
      const base = lane === 0 ? history[0]?.value : 1;
      return months.map((month, index) => index <= reveal ? (() => { const raw = history.find((point) => point.date.slice(0, 7) === month)?.value; return raw == null ? null : lane === 0 && base ? raw / base * 100 : raw; })() : null);
    });
    const top = (index: number) => 52 + index * 130;
    return {
      aria: { enabled: true, decal: { show: true }, description: "Base monetaria normalizada, inflación, actividad y salario real en cuatro carriles con escalas separadas." },
      animationDurationUpdate: reducedMotion ? 0 : 780,
      tooltip: { ...tooltip, trigger: "axis", axisPointer: { type: "line", link: [{ xAxisIndex: "all" }] } },
      title: titles.map((title, index) => ({ text: title, subtext: units[index], left: 8, top: top(index) - 24, textStyle: { color: colors[index], fontSize: 11, fontWeight: 800 }, subtextStyle: { color: "#94a3b8", fontSize: 9 } })),
      grid: titles.map((_, index) => ({ left: 170, right: 28, top: top(index), height: 86 })),
      xAxis: titles.map((_, index) => ({ type: "category", gridIndex: index, data: months.map((month) => month.slice(2)), boundaryGap: false, ...axis, axisLabel: { ...chartText, show: index === titles.length - 1 } })),
      yAxis: titles.map((_, index) => ({ type: "value", gridIndex: index, name: units[index], nameLocation: "middle", nameGap: 55, ...axis, nameTextStyle: { ...chartText, fontSize: 9 }, axisLabel: { ...chartText, fontSize: 10, formatter: index === 0 ? "{value}" : "{value}%" } })),
      series: titles.map((title, index) => ({ name: title, type: "line", xAxisIndex: index, yAxisIndex: index, data: values[index], connectNulls: false, smooth: .18, symbolSize: 6, lineStyle: neonLine(colors[index]), itemStyle: { color: "#fff", borderColor: colors[index], borderWidth: 2 }, areaStyle: { color: `${colors[index]}12` }, tooltip: { valueFormatter: (value: number) => `${Number(value).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}${index === 0 ? " índice" : "%"}` } }))
    };
  }, [data, macroFrame, reducedMotion]);

  if (!data && loading)
    return (
      <div className="splash">
        <img src="/montiel-logo.png" alt="MONTIEL 2030" />
        <span>Cargando inteligencia económica…</span>
      </div>
    );
  if (!data)
    return (
      <div className="splash error">
        <WifiOff />
        {error || "No hay datos"}
        <button onClick={() => load()}>Reintentar</button>
      </div>
    );

  const liveCount = data.sources.filter(
    (source) => source.state === "LIVE" || source.state === "ACTUALIZADO",
  ).length;
  const sourceWeight = {
    LIVE: 100,
    ACTUALIZADO: 90,
    PARCIAL: 65,
    DEMORADO: 45,
    ERROR: 0,
  } as const;
  const qualityScore = Math.round(
    data.sources.reduce((sum, source) => sum + sourceWeight[source.state], 0) /
      Math.max(1, data.sources.length),
  );
  const nextEvent = data.calendar.find(
    (event) => event.date >= new Date().toISOString().slice(0, 10),
  );
  const tradeDisplayHistory = (metric("exports")?.history ?? []).filter((point) => point.date >= "2025-01");
  const tradeDisplayIndex = Math.min(macroFrame, Math.max(0, tradeDisplayHistory.length - 1));
  const tradeDisplayMonth = tradeDisplayHistory[tradeDisplayIndex]?.date;
  const tradeDisplay = {
    month: tradeDisplayMonth ?? "s/d",
    exports: tradeDisplayHistory[tradeDisplayIndex]?.value,
    imports: metric("imports")?.history?.find((point) => point.date.slice(0, 7) === tradeDisplayMonth?.slice(0, 7))?.value,
    balance: metric("trade_balance")?.history?.find((point) => point.date.slice(0, 7) === tradeDisplayMonth?.slice(0, 7))?.value,
  };
  const baseMotionHistory = metric("base_money")?.frequency === "DIARIA"
    ? monthlyAverage(metric("base_money")?.history ?? []).filter((point) => point.date >= "2025-01")
    : (metric("base_money")?.history ?? []).filter((point) => point.date >= "2025-01");
  const tape = [
    {
      label: "DÓLAR MAYORISTA BCRA",
      value: metric("fx")?.display ?? "s/d",
      tag: metric("fx")?.status ?? "REAL",
    },
    {
      label: "REM AGOSTO",
      value: metric("rem_aug")?.display ?? "s/d",
      tag: "EXPECTATIVA",
    },
    {
      label: "REM DICIEMBRE",
      value: metric("rem_dec")?.display ?? "s/d",
      tag: "EXPECTATIVA",
    },
    {
      label: "IPC MENSUAL",
      value: metric("ipc")?.display ?? "s/d",
      tag: metric("ipc")?.status ?? "REAL",
    },
    {
      label: "TASA",
      value: metric("rate")?.display ?? "s/d",
      tag: metric("rate")?.status ?? "EXPECTATIVA",
    },
    {
      label: "BASE MONETARIA BCRA",
      value: metric("base_money")?.display ?? "s/d",
      tag: metric("base_money")?.status ?? "REAL",
    },
    {
      label: "SALDO COMERCIAL",
      value: metric("trade_balance")?.display ?? "s/d",
      tag: metric("trade_balance")?.period ?? "ICA",
    },
    {
      label: "PRÓXIMA PUBLICACIÓN",
      value: nextEvent
        ? `${nextEvent.agency} · ${nextEvent.title} · ${nextEvent.date}`
        : "s/d",
      tag: "CALENDARIO",
    },
    {
      label: "HORA ARGENTINA",
      value: new Intl.DateTimeFormat("es-AR", {
        timeStyle: "medium",
        timeZone: "America/Argentina/Buenos_Aires",
      }).format(clock),
      tag: "UTC−3",
    },
  ];

  return (
    <div className={reducedMotion ? "app reduce-motion" : "app"}>
      <div className="cursor-halo" ref={haloRef} aria-hidden="true" />
      <header className="hero">
        <div className="brand">
          <img src="/montiel-logo.png" alt="Logotipo MONTIEL 2030" />
          <div>
            <span>MONTIEL 2030 · DECISION INTELLIGENCE</span>
            <h1>Argentina Economic Intelligence</h1>
            <p>Datos oficiales, alertas y proyecciones para decidir antes.</p>
          </div>
        </div>
        <div className="hero-actions">
          <div className={`live-pill ${data.live ? "online" : "offline"}`}>
            {data.live ? <Wifi /> : <WifiOff />}
            <span>{data.live ? "LIVE" : "RESPALDO"}</span>
          </div>
          <button
            className="refresh"
            onClick={() => load(true)}
            disabled={loading}
          >
            <RefreshCw className={loading ? "spin" : ""} />
            Actualizar ahora
          </button>
          <a
            className="consulting-cta"
            href="https://wa.me/543624000857?text=Quiero%20un%20dashboard%20inteligente%20para%20mi%20organizacion"
            target="_blank"
            rel="noreferrer"
          >
            <Sparkles />
            Quiero mi tablero
          </a>
        </div>
        <div className="status-line">
          <span>
            <Database />
            {liveCount}/{data.sources.length} fuentes conectadas
          </span>
          <span>
            <CheckCircle2 />
            Actualizado: {formatDate(data.updatedAt)}
          </span>
          <span>
            <CalendarDays />
            Próxima revisión: {formatDate(data.nextUpdateAt)}
          </span>
          <label>
            <input
              type="checkbox"
              checked={reducedMotion}
              onChange={(e) => setReducedMotion(e.target.checked)}
            />
            Reducir movimiento
          </label>
        </div>
      </header>

      <div className="market-tape" aria-label="Cinta de indicadores económicos">
        <div className="tape-track">
          {[...tape, ...tape].map((item, index) => (
            <div className="tape-item" key={`${item.label}-${index}`}>
              <small>{item.label}</small>
              <b>{item.value}</b>
              <span>{item.tag}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="search-shell">
        <Search />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar indicadores, fuentes o publicaciones…"
          aria-label="Buscar indicadores y publicaciones"
        />
        <span>⌘ K</span>
        <AnimatePresence>
          {results.length > 0 && (
            <motion.div
              className="search-results"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {results.map((result, index) => (
                <a
                  href={result.url}
                  target="_blank"
                  rel="noreferrer"
                  key={`${result.title}-${index}`}
                >
                  <small>{result.type}</small>
                  <b>{result.title}</b>
                  <span>{result.subtitle}</span>
                </a>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <nav className="tabs" aria-label="Secciones">
        {tabs.map((tab) => (
          <button
            key={tab}
            className={active === tab ? "active" : ""}
            onClick={() => setActive(tab)}
          >
            {tab}
          </button>
        ))}
      </nav>

      <main>
        {error && (
          <div className="banner-error">
            <AlertTriangle />
            La actualización falló: {error}. Se conserva el último dato válido.
          </div>
        )}
        {active === "Resumen Ejecutivo" && (
          <>
            <SectionIntro
              eyebrow="LECTURA DE DIRECTORIO"
              title="Qué necesita decidir la conducción hoy"
              description="Una síntesis de precios, demanda y condiciones financieras. Cada cifra conserva período, unidad, clase y enlace a la fuente oficial."
              decision="Priorizar caja, reposición y precios sin confundir hechos con expectativas o modelos."
            />
            <section className="kpi-grid">
              {["ipc", "sales", "fx", "rate"]
                .map((id, index) => metric(id))
                .filter((item): item is Metric => Boolean(item))
                .map((item, index) => (
                  <KpiCard metric={item} index={index} key={item.id} />
                ))}
            </section>
            <section className="decision-board">
              <DecisionCard
                status="VIGILAR"
                tone="watch"
                title="Precios todavía avanzan"
                evidence={`IPC: ${metric("ipc")?.display ?? "s/d"} mensual en ${metric("ipc")?.period ?? "el último período"}.`}
                action="Revisar listas y costos con la próxima publicación; no ajustar por un mes aislado."
              />
              <DecisionCard
                status="RIESGO DE DEMANDA"
                tone="risk"
                title="El consumo PyME sigue débil"
                evidence={`Ventas: ${metric("sales")?.display ?? "s/d"} interanual en ${metric("sales")?.period ?? "el último período"}.`}
                action="Proteger caja y margen; segmentar promociones en lugar de descontar todo."
              />
              <DecisionCard
                status="PLANIFICAR"
                tone="ok"
                title="Dólar y tasa requieren rango"
                evidence={`Mayorista: ${metric("fx")?.display ?? "s/d"}. Expectativa REM dic.: ${metric("rem_dec")?.display ?? "s/d"}.`}
                action="Presupuestar escenario base y adverso; una expectativa REM no es garantía."
              />
            </section>
            <div className="dashboard-grid">
              <Panel
                title="Pulso económico para directorio: precios, consumo y costo de deuda"
                kicker="MOTION CHART · TRES ESCALAS SEPARADAS"
                icon={<Activity />}
                wide
              >
                <ReactECharts
                  option={boardPulseOption}
                  style={{ height: 525 }}
                />
                <div className="motion-controls">
                  <button onClick={() => setPlaying(!playing)}>
                    {playing ? <Pause /> : <Play />}
                    {playing ? "Pausar" : "Reproducir"}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="11"
                    value={Math.min(projectionFrame, 11)}
                    onChange={(e) => {
                      setPlaying(false);
                      setProjectionFrame(Number(e.target.value));
                    }}
                  />
                  <strong>
                    {
                      [
                        "Ene",
                        "Feb",
                        "Mar",
                        "Abr",
                        "May",
                        "Jun",
                        "Jul",
                        "Ago",
                        "Sep",
                        "Oct",
                        "Nov",
                        "Dic",
                      ][Math.min(projectionFrame, 11)]
                    }{" "}
                    2026
                  </strong>
                </div>
                <div className="plain-guide">
                  <b>Cómo leerlo</b>
                  <p>
                    Los tres paneles avanzan por el mismo mes, pero cada uno
                    conserva su propia unidad.{" "}
                    <strong>
                      No se suman ni se comparan las alturas entre paneles.
                    </strong>{" "}
                    La línea cian es inflación observada; la punteada oro es
                    modelo; rosa son ventas reales; verde es tasa real y los
                    diamantes oro son expectativas REM.
                  </p>
                </div>
                <ChartContract
                  x="Mes calendario de 2026, igual para los tres paneles"
                  y="Panel 1: % mensual · Panel 2: % interanual · Panel 3: % TNA"
                  encoding="Línea continua: dato real · Punteada: modelo · Diamante: expectativa REM · Pulso: mes seleccionado"
                  period="Enero–diciembre 2026 según disponibilidad"
                  sources="INDEC + CAME + BCRA/REM"
                  caution="Ventas futuras quedan vacías: no hay muestra suficiente para una proyección validada. TAMAR es proxy del costo de deuda, no deuda total."
                />
              </Panel>
            </div>
          </>
        )}

        {active === "Precios y Costos" && (
          <>
            <SectionIntro
              eyebrow="INFLACIÓN Y ESTRUCTURA DE COSTOS"
              title="Qué sube, en qué etapa y en qué región"
              description="Esta página compara precios al consumidor, precios mayoristas, alimentos y diferencias regionales. No repite el resumen ejecutivo."
              decision="Distinguir presión de costos de inflación al consumidor antes de modificar precios o presupuestos."
            />
            <section className="kpi-grid">
              {["ipc", "ipc_yoy", "ipim", "cba"]
                .map((id) => metric(id))
                .filter((item): item is Metric => Boolean(item))
                .map((item, index) => (
                  <KpiCard metric={item} index={index} key={item.id} />
                ))}
            </section>
            <div className="dashboard-grid">
              <Panel
                title="Inflación al consumidor y precios mayoristas por mes"
                kicker="PRECIOS Y COSTOS"
                icon={<LineChart />}
              >
                <ReactECharts
                  option={comparisonOption}
                  style={{ height: 350 }}
                />
                <ChartContract
                  x="Mes publicado"
                  y="Variación respecto del mes anterior (%)"
                  encoding="Cian: IPC consumidor · Rosa: IPIM mayorista"
                  period="IPC a jul-26 · IPIM a jun-26"
                  sources="INDEC"
                  caution="El IPIM tiene un mes menos. El tablero no inventa julio ni interpreta la brecha como margen empresarial."
                />
                <Insight
                  what={`IPC ${metric("ipc")?.display} en julio; IPIM ${metric("ipim")?.display} en junio.`}
                  why="Miden etapas y períodos diferentes de la cadena de precios."
                  action="Comparar el costo propio con el IPIM de su sector antes de trasladar precios."
                  tone="rose"
                />
              </Panel>
              <Panel
                title="Inflación general y vivienda por región"
                kicker="LECTURA TERRITORIAL · JULIO 2026"
                icon={<BarChart3 />}
              >
                <ReactECharts option={regionalOption} style={{ height: 350 }} />
                <ChartContract
                  x="Variación respecto de junio (%)"
                  y="Región estadística de INDEC"
                  encoding="Azul: nivel general · Oro: Vivienda, agua, electricidad y combustibles"
                  period="Julio 2026"
                  sources="INDEC · IPC julio 2026"
                  caution="Son variaciones del mes, no precios en pesos ni costo de vida total."
                />
                <Insight
                  what="NEA: Vivienda 3,9% mensual frente a 2,0% en el nivel general."
                  why="La composición regional modifica la presión sobre hogares y empresas."
                  action="Usar la región del negocio; no aplicar un promedio nacional de forma automática."
                  tone="gold"
                />
              </Panel>
            </div>
          </>
        )}

        {active === "Dólar y Finanzas" && (
          <>
            <SectionIntro
              eyebrow="TIPO DE CAMBIO Y COSTO FINANCIERO"
              title="Exposición cambiaria y referencia para deuda"
              description="Separa el tipo de cambio observado, las expectativas privadas relevadas por BCRA y la tasa bancaria. No presenta expectativas como hechos."
              decision="Definir cobertura, calendario de compras y sensibilidad del servicio de deuda."
            />
            <section className="kpi-grid">
              {["fx", "rem_aug", "rem_dec", "rate"]
                .map((id) => metric(id))
                .filter((item): item is Metric => Boolean(item))
                .map((item, index) => (
                  <KpiCard metric={item} index={index} key={item.id} />
                ))}
            </section>
            <div className="dashboard-grid">
              <Panel
                title="Tipo de cambio: histórico observado y modelo cuando existe muestra"
                kicker="BCRA · PROYECCIÓN CONDICIONADA"
                icon={<DollarSign />}
                wide
              >
                {metric("fx") && (
                  <ForecastChart metric={metric("fx")!} horizon={4} />
                )}
                <Insight
                  what={`BCRA ${metric("fx")?.display} · REM dic ${metric("rem_dec")?.display}`}
                  why="La diferencia entre dato y expectativa altera reposición, importaciones y caja."
                  action="Evaluar base y adverso; no comprar sólo por una mediana de encuesta."
                  tone="emerald"
                />
              </Panel>
            </div>
          </>
        )}

        {active === "Proyecciones" && (
          <>
            <SectionIntro
              eyebrow="MODELOS Y ESCENARIOS"
              title="Evolución de enero a diciembre de 2026"
              description="La trayectoria observada se extiende sólo cuando el modelo supera un control fuera de muestra. Los meses futuros se distinguen por línea punteada y bandas."
              decision="Trabajar con rangos y gatillos de revisión, no con un número futuro único."
            />
            <div className="dashboard-grid">
              <Panel
                title="Inflación mensual: datos reales y trayectoria modelada a diciembre"
                kicker="FAN CHART · CHAMPION–CHALLENGER"
                icon={<Sparkles />}
                wide
              >
                {metric("ipc") && (
                  <ForecastChart metric={metric("ipc")!} horizon={5} />
                )}
                <Insight
                  what="Compiten Naive, Deriva y Holt amortiguado; gana el menor RMSE fuera de muestra."
                  why="Un modelo complejo no se acepta si no mejora el baseline en la misma muestra."
                  action="Usar bandas 80/95% y reestimar al publicarse cada IPC."
                />
              </Panel>
              <Panel
                title="Exportaciones mensuales: trayectoria probabilística hasta diciembre"
                kicker="COMERCIO EXTERIOR · FOB · MODELO INDEPENDIENTE"
                icon={<TrendingUp />}
                wide
              >
                {metric("exports") && <ForecastChart metric={metric("exports")!} horizon={6} />}
                <Insight
                  what={`Último observado: ${metric("exports")?.display ?? "s/d"}. REM anual 2026: ${metric("rem_exports_2026")?.display ?? "s/d"}.`}
                  why="El REM anual funciona como contraste de consistencia, no como dato mensual ni como meta."
                  action="Reestimar con cada ICA y rechazar el modelo si deja de superar el baseline."
                  tone="emerald"
                />
              </Panel>
              <Panel
                title="Importaciones mensuales: trayectoria probabilística hasta diciembre"
                kicker="COMERCIO EXTERIOR · CIF · MODELO INDEPENDIENTE"
                icon={<TrendingDown />}
                wide
              >
                {metric("imports") && <ForecastChart metric={metric("imports")!} horizon={6} />}
                <Insight
                  what={`Último observado: ${metric("imports")?.display ?? "s/d"}. REM anual 2026: ${metric("rem_imports_2026")?.display ?? "s/d"}.`}
                  why="Importaciones responden a actividad, tipo de cambio y restricciones; una extrapolación no identifica causalidad."
                  action="Usar el rango para caja en divisas y compras; no comprometerse con el centro como certeza."
                  tone="gold"
                />
              </Panel>
              <Panel
                title="Suficiencia, rezagos y habilitación de variables"
                kicker="PUERTA DE CALIDAD ANTES DEL MODELO"
                icon={<Database />}
                wide
              >
                <DataSufficiencyTable data={data} />
              </Panel>
              <Panel
                title="Registro ejecutivo del modelo"
                kicker="M-O-S-A-I-C · TRAZABILIDAD"
                icon={<ShieldCheck />}
                wide
              >
                <div className="model-register">
                  <div>
                    <small>OBJETIVO</small>
                    <b>IPC mensual nacional</b>
                    <p>% respecto del mes anterior · horizonte ago–dic 2026.</p>
                  </div>
                  <div>
                    <small>DATOS</small>
                    <b>INDEC · {metric("ipc")?.history?.[0]?.date ?? "s/d"}–{metric("ipc")?.history?.at(-1)?.date ?? "s/d"}</b>
                    <p>
                      {metric("ipc")?.history?.length ?? 0} observaciones oficiales de la API; sin interpolar.
                    </p>
                  </div>
                  <div>
                    <small>SELECCIÓN</small>
                    <b>Champion–challenger</b>
                    <p>Naive, Deriva y Holt amortiguado con origen rodante.</p>
                  </div>
                  <div>
                    <small>VALIDACIÓN</small>
                    <b>MAE · RMSE · sMAPE · sesgo</b>
                    <p>
                      Comparación temporal fuera de muestra; sin fuga de futuro.
                    </p>
                  </div>
                  <div>
                    <small>INCERTIDUMBRE</small>
                    <b>Bootstrap residual 10.000×</b>
                    <p>
                      Percentiles 80% y 95%, semilla fija; estado según
                      evidencia.
                    </p>
                  </div>
                  <div>
                    <small>INVALIDACIÓN</small>
                    <b>Quiebre o dato nuevo</b>
                    <p>Reestimar después de cada publicación oficial.</p>
                  </div>
                </div>
              </Panel>
              <Panel
                title="Fórmulas que realmente ejecuta el motor"
                kicker="LIBRO MAYOR CUANTITATIVO"
                icon={<Gauge />}
                wide
              >
                <FormulaLedger />
              </Panel>
              <Panel
                title="Modelos modernos: cuáles están activos y cuáles todavía no"
                kicker="MATRIZ DE ELEGIBILIDAD · SIN HUMO"
                icon={<Database />}
                wide
              >
                <QuantitativeModelRegistry />
                <p className="chart-disclaimer">
                  La sofisticación no sustituye evidencia. TCN, redes
                  bayesianas, Kyle, Minsky o árboles sólo se activarán con los
                  datos que exige cada método y si superan un baseline mediante
                  validación temporal reproducible.
                </p>
              </Panel>
              <Panel
                title="Tres capas de evidencia, sin mezclarlas"
                kicker="OFICIAL · CONSENSO · MODELO"
                icon={<ShieldCheck />}
                wide
              >
                <div className="evidence-layers">
                  <article>
                    <small>1 · OBSERVADO</small>
                    <b>INDEC / Datos Argentina</b>
                    <p>
                      IPC, EMAE, IPIM, comercio exterior y base monetaria con
                      IDs de serie, período, unidad y enlace. Sólo esta capa se
                      rotula REAL.
                    </p>
                  </article>
                  <article>
                    <small>2 · CONSENSO</small>
                    <b>BCRA · REM junio (44 participantes)</b>
                    <p>
                      Medianas de 33 consultoras/centros y 12 entidades
                      financieras. Son EXPECTATIVAS, no pronósticos propios del
                      BCRA.
                    </p>
                  </article>
                  <article>
                    <small>3 · MODELO MONTIEL</small>
                    <b>Champion–challenger + bootstrap</b>
                    <p>
                      Pronóstico condicionado a la muestra, con error, bandas y
                      regla de invalidación visibles.
                    </p>
                  </article>
                </div>
              </Panel>
              <Panel
                title="¿Qué tan lejos está Argentina? Composición del PIB y cierre 2026"
                kicker="C + G + I + XN + RESIDUAL = 100 · COMPARACIÓN AUDITADA"
                icon={<BarChart3 />}
                wide
              >
                <GdpCompositionPanel data={data} />
              </Panel>
            </div>
          </>
        )}

        {active === "Laboratorio" && (
          <>
            <SectionIntro
              eyebrow="SIMULACIÓN EMPRESARIAL"
              title="Probá la resistencia de tu negocio"
              description="Modificá ventas, costos, deuda, tasa, inflación y dólar. Todos los resultados son escenarios matemáticos construidos con tus supuestos."
              decision="Identificar punto de equilibrio, margen de seguridad y condiciones que vuelven negativa la caja."
            />
            <DecisionLab data={data} reducedMotion={reducedMotion} />
          </>
        )}

        {active === "Análisis Visual" && (
          <>
            <SectionIntro
              eyebrow="ANÁLISIS VISUAL EXPLICADO"
              title="Relaciones económicas que se entienden sin jerga"
              description="Cada gráfico indica qué representa la horizontal, la vertical, el color, el movimiento, el período y la fuente. Los valores esenciales también aparecen fuera del tooltip."
              decision="Explorar asociaciones y trayectorias sin confundirlas con causalidad."
            />
            <div className="dashboard-grid">
              <Panel
                title="Siete señales para distinguir desinflación sana de falta de demanda"
                kicker="MOTION CHART · 7 CARRILES · UN EJE DE TIEMPO"
                icon={<Activity />}
                wide
              >
                <ReactECharts
                  option={sevenSignalsOption}
                  style={{ height: 825 }}
                />
                <div className="motion-controls">
                  <button onClick={() => setPlaying(!playing)}>
                    {playing ? <Pause /> : <Play />}
                    {playing ? "Pausar" : "Reproducir"}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="11"
                    value={Math.min(projectionFrame, 11)}
                    onChange={(e) => {
                      setPlaying(false);
                      setProjectionFrame(Number(e.target.value));
                    }}
                  />
                  <strong>
                    {
                      [
                        "Ene",
                        "Feb",
                        "Mar",
                        "Abr",
                        "May",
                        "Jun",
                        "Jul",
                        "Ago",
                        "Sep",
                        "Oct",
                        "Nov",
                        "Dic",
                      ][Math.min(projectionFrame, 11)]
                    }{" "}
                    2026
                  </strong>
                </div>
                <div className="alarm-card active">
                  <div>
                    <AlertTriangle />
                    <span>SEÑAL DE ALERTA ACTIVA · LECTURA PROVISIONAL</span>
                  </div>
                  <h3>
                    La inflación desacelera, pero consumo y actividad todavía no
                    confirman una recuperación sostenida.
                  </h3>
                  <p>
                    Regla visible: si el IPC se mantiene aproximadamente en 2%
                    mensual y, al mismo tiempo, ventas PyME y EMAE
                    desestacionalizado no son positivos en sus últimos datos
                    disponibles, no interpretar cada décima menos de inflación
                    como mejora inequívoca.
                  </p>
                  <ul>
                    <li>
                      <b>IPC:</b> 2,1% mensual en julio.
                    </li>
                    <li>
                      <b>Consumo PyME:</b> −3,8% interanual en julio.
                    </li>
                    <li>
                      <b>Actividad EMAE:</b> −0,5% mensual desestacionalizado en
                      mayo.
                    </li>
                  </ul>
                  <small>
                    Esta es una regla de decisión, no una probabilidad. La
                    probabilidad conjunta a diciembre no se publica porque
                    salario real, deuda/ingreso, morosidad, consumo, actividad e
                    inversión no tienen todavía una muestra mensual homogénea y
                    contemporánea suficiente para calibrarla sin fabricar
                    precisión.
                  </small>
                </div>
                <SignalCoverageTable data={data} />
                <ChartContract
                  x="Mes calendario 2026 compartido por los siete carriles"
                  y="Cada carril conserva su unidad: mensual, interanual, TNA o trimestral"
                  encoding="Continua: observado/calculado · Oro punteada: IPC modelado · Hueco: dato aún no publicado o serie no disponible"
                  period="Enero–diciembre 2026; el futuro sólo aparece donde existe modelo validado"
                  sources="INDEC + BCRA + CAME; salario real calculado con fórmula visible"
                  caution="No compare alturas entre carriles. Deuda/ingreso queda vacía porque no existe una serie mensual oficial homogénea; inversión es trimestral y no se interpola."
                />
              </Panel>
              <Panel
                title="Comercio exterior en movimiento: exportaciones, importaciones y saldo"
                kicker="MOTION CHART · SECTOR EXTERNO OFICIAL"
                icon={<Activity />}
                wide
              >
                <ReactECharts option={tradeMotionOption} style={{ height: 460 }} />
                <div className="motion-readout" aria-live="polite">
                  <span>MES<b>{tradeDisplay.month}</b></span>
                  <span>EXPORTACIONES FOB<b>{tradeDisplay.exports == null ? "s/d" : `USD ${tradeDisplay.exports.toLocaleString("es-AR", { maximumFractionDigits: 0 })} M`}</b></span>
                  <span>IMPORTACIONES CIF<b>{tradeDisplay.imports == null ? "s/d" : `USD ${tradeDisplay.imports.toLocaleString("es-AR", { maximumFractionDigits: 0 })} M`}</b></span>
                  <span>SALDO COMERCIAL<b>{tradeDisplay.balance == null ? "s/d" : `USD ${tradeDisplay.balance.toLocaleString("es-AR", { maximumFractionDigits: 0 })} M`}</b></span>
                </div>
                <div className="motion-controls">
                  <button onClick={() => setPlaying(!playing)}>{playing ? <Pause /> : <Play />}{playing ? "Pausar" : "Reproducir"}</button>
                  <input type="range" min="0" max={Math.max(0, (metric("exports")?.history?.filter((point) => point.date >= "2025-01").length ?? 1) - 1)} value={Math.min(macroFrame, Math.max(0, (metric("exports")?.history?.filter((point) => point.date >= "2025-01").length ?? 1) - 1))} onChange={(event) => { setPlaying(false); setMacroFrame(Number(event.target.value)); }} />
                  <strong>{metric("exports")?.history?.filter((point) => point.date >= "2025-01")[Math.min(macroFrame, Math.max(0, (metric("exports")?.history?.filter((point) => point.date >= "2025-01").length ?? 1) - 1))]?.date ?? "s/d"}</strong>
                </div>
                <ChartContract
                  x="Importaciones mensuales de bienes, valor CIF, millones de USD corrientes"
                  y="Exportaciones mensuales de bienes, valor FOB, millones de USD corrientes"
                  encoding="Pulso verde: superávit · Rosa: déficit · Tamaño: magnitud absoluta del saldo · Línea: trayectoria mensual"
                  period="Desde enero de 2025 hasta el último ICA disponible"
                  sources="INDEC / Datos Argentina · series 74.3_IET_0_M_16, 74.3_IIT_0_M_25 y 74.3_ISC_0_M_19"
                  caution="FOB y CIF son bases de valuación distintas pero corresponden a la definición oficial del saldo comercial. Valores corrientes: no miden volumen físico."
                />
              </Panel>
              <Panel
                title="Transmisión monetaria y economía real: cuatro escalas sincronizadas"
                kicker="MOTION CHART · DINERO, PRECIOS, ACTIVIDAD Y SALARIO"
                icon={<LineChart />}
                wide
              >
                <ReactECharts option={monetaryTransmissionOption} style={{ height: 610 }} />
                <div className="motion-controls">
                  <button onClick={() => setPlaying(!playing)}>{playing ? <Pause /> : <Play />}{playing ? "Pausar" : "Reproducir"}</button>
                  <input type="range" min="0" max={Math.max(0, baseMotionHistory.length - 1)} value={Math.min(macroFrame, Math.max(0, baseMotionHistory.length - 1))} onChange={(event) => { setPlaying(false); setMacroFrame(Number(event.target.value)); }} />
                  <strong>Secuencia mensual desde ene-2025</strong>
                </div>
                <ChartContract
                  x="Mes calendario sincronizado entre los cuatro carriles"
                  y="Base monetaria: índice ene-2025=100 · IPC, EMAE y salario real: variación mensual (%)"
                  encoding="Oro: base monetaria · Cian: IPC · Verde: EMAE · Violeta: salario real calculado"
                  period="Desde enero de 2025 hasta el último dato de cada fuente; los huecos expresan rezago de publicación"
                  sources="BCRA API Monetarias v4 + INDEC / Datos Argentina"
                  caution="La simultaneidad visual no prueba causalidad. Base monetaria se normaliza sólo para leer su trayectoria; no se suma con porcentajes."
                />
              </Panel>
              <Panel
                title="Inflación mensual y ventas PyME interanuales: posición de cada mes"
                kicker="MOTION CHART · DATOS OBSERVADOS"
                icon={<Activity />}
                wide
              >
                <ReactECharts option={motionOption} style={{ height: 430 }} />
                <div className="motion-readout">
                  <span>
                    MES{" "}
                    <b>
                      {
                        data.consumption[
                          Math.min(frame, data.consumption.length - 1)
                        ]?.date
                      }
                    </b>
                  </span>
                  <span>
                    INFLACIÓN MENSUAL{" "}
                    <b>
                      {
                        data.consumption[
                          Math.min(frame, data.consumption.length - 1)
                        ]?.ipc
                      }
                      %
                    </b>
                  </span>
                  <span>
                    VENTAS VS. MISMO MES 2025{" "}
                    <b>
                      {
                        data.consumption[
                          Math.min(frame, data.consumption.length - 1)
                        ]?.sales
                      }
                      %
                    </b>
                  </span>
                  <span>
                    CLASE <b>REAL</b>
                  </span>
                </div>
                <div className="motion-controls">
                  <button onClick={() => setPlaying(!playing)}>
                    {playing ? <Pause /> : <Play />}
                    {playing ? "Pausar" : "Reproducir"}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max={data.consumption.length - 1}
                    value={frame}
                    onChange={(e) => {
                      setPlaying(false);
                      setFrame(Number(e.target.value));
                    }}
                  />
                  <strong>{data.consumption[frame]?.date}</strong>
                </div>
                <ChartContract
                  x="Inflación del mes: más a la derecha = mayor inflación"
                  y="Ventas reales contra igual mes de 2025: arriba = mejor desempeño"
                  encoding="Burbuja luminosa: mes seleccionado · Línea: recorrido desde enero"
                  period="Enero–julio 2026"
                  sources="IPC nacional INDEC + ventas minoristas PyME a precios constantes CAME"
                  caution="Una coordenada usa variación mensual y la otra interanual. Muestra asociación, no demuestra que la inflación cause las ventas."
                />
                <Insight
                  what="El tamaño de la burbuja es fijo; no representa otra variable."
                  why="Así se evita atribuir significado a un dato inexistente."
                  action="Leer primero mes, unidades y fuentes; luego evaluar la trayectoria."
                  tone="violet"
                />
              </Panel>
              <Panel
                title="Inflación al consumidor y mayorista: cómo se mueve la presión de precios"
                kicker="MOTION CHART · DOBLE ÍNDICE INDEC"
                icon={<LineChart />}
                wide
              >
                <ReactECharts
                  option={priceMotionOption}
                  style={{ height: 430 }}
                />
                <div className="motion-readout">
                  <span>
                    MES{" "}
                    <b>
                      {
                        ["Ene", "Feb", "Mar", "Abr", "May", "Jun"][
                          Math.min(frame, 5)
                        ]
                      }{" "}
                      2026
                    </b>
                  </span>
                  <span>
                    IPC CONSUMIDOR{" "}
                    <b>
                      {metric("ipc")?.history?.filter((point) =>
                        point.date.startsWith("2026"),
                      )[Math.min(frame, 5)]?.value ?? "s/d"}
                      %
                    </b>
                  </span>
                  <span>
                    IPIM MAYORISTA{" "}
                    <b>
                      {metric("ipim")?.history?.[Math.min(frame, 5)]?.value ??
                        "s/d"}
                      %
                    </b>
                  </span>
                  <span>
                    FUENTE <b>INDEC</b>
                  </span>
                </div>
                <div className="motion-controls">
                  <button onClick={() => setPlaying(!playing)}>
                    {playing ? <Pause /> : <Play />}
                    {playing ? "Pausar" : "Reproducir"}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="5"
                    value={Math.min(frame, 5)}
                    onChange={(e) => {
                      setPlaying(false);
                      setFrame(Number(e.target.value));
                    }}
                  />
                  <strong>
                    {
                      ["Ene", "Feb", "Mar", "Abr", "May", "Jun"][
                        Math.min(frame, 5)
                      ]
                    }{" "}
                    2026
                  </strong>
                </div>
                <ChartContract
                  x="Inflación al consumidor del mes: más a la derecha = mayor"
                  y="Inflación mayorista del mes: más arriba = mayor"
                  encoding="Pulso: mes observado · Línea: recorrido desde enero"
                  period="Enero–junio 2026"
                  sources="INDEC IPC nacional + INDEC IPIM"
                  caution="Ambas son variaciones mensuales, pero miden etapas distintas. Julio IPIM no se completa ni se estima."
                />
              </Panel>
              <Panel
                title="Inflación 2026: de los datos publicados a un rango para diciembre"
                kicker="MOTION FORECAST · REAL → MODELO"
                icon={<Sparkles />}
                wide
              >
                {ipcProjectionMotion.length ? (
                  <>
                    <ReactECharts
                      option={ipcProjectionMotionOption}
                      style={{ height: 430 }}
                    />
                    <div className="motion-readout">
                      <span>
                        MES{" "}
                        <b>
                          {
                            ipcProjectionMotion[
                              Math.min(
                                projectionFrame,
                                ipcProjectionMotion.length - 1,
                              )
                            ]?.date
                          }
                        </b>
                      </span>
                      <span>
                        INFLACIÓN MENSUAL{" "}
                        <b>
                          {ipcProjectionMotion[
                            Math.min(
                              projectionFrame,
                              ipcProjectionMotion.length - 1,
                            )
                          ]?.value.toFixed(2)}
                          %
                        </b>
                      </span>
                      <span>
                        CLASE{" "}
                        <b>
                          {
                            ipcProjectionMotion[
                              Math.min(
                                projectionFrame,
                                ipcProjectionMotion.length - 1,
                              )
                            ]?.status
                          }
                        </b>
                      </span>
                      <span>
                        INCERTIDUMBRE 95%{" "}
                        <b>
                          {ipcProjectionMotion[
                            Math.min(
                              projectionFrame,
                              ipcProjectionMotion.length - 1,
                            )
                          ]?.status === "REAL"
                            ? "No aplica"
                            : `ancho total ${ipcProjectionMotion[Math.min(projectionFrame, ipcProjectionMotion.length - 1)]?.width95.toFixed(2)} pp`}
                        </b>
                      </span>
                    </div>
                    <div className="motion-controls">
                      <button onClick={() => setPlaying(!playing)}>
                        {playing ? <Pause /> : <Play />}
                        {playing ? "Pausar" : "Reproducir"}
                      </button>
                      <input
                        type="range"
                        min="0"
                        max={ipcProjectionMotion.length - 1}
                        value={Math.min(
                          projectionFrame,
                          ipcProjectionMotion.length - 1,
                        )}
                        onChange={(e) => {
                          setPlaying(false);
                          setProjectionFrame(Number(e.target.value));
                        }}
                      />
                      <strong>
                        {
                          ipcProjectionMotion[
                            Math.min(
                              projectionFrame,
                              ipcProjectionMotion.length - 1,
                            )
                          ]?.date
                        }
                      </strong>
                    </div>
                    <ChartContract
                      x="Mes de 2026: 1 es enero y 12 es diciembre"
                      y="Inflación del mes (% mensual; puntos porcentuales sobre el eje)"
                      encoding="Cian continua: dato INDEC · Oro punteada: modelo · Burbuja futura más grande: mayor ancho total del rango 95%"
                      period="Enero–diciembre 2026"
                      sources="INDEC/Datos Argentina desde ene 2025 + motor champion–challenger v1.0.7"
                      caution="Agosto–diciembre son resultados de modelo, no datos oficiales, metas ni certezas. La burbuja futura representa incertidumbre, no más inflación."
                    />
                  </>
                ) : (
                  <div className="empty scientific">
                    <AlertTriangle />
                    <div>
                      <b>PROYECCIÓN NO HABILITADA</b>
                      <span>
                        La fuente no aportó 12 observaciones oficiales
                        utilizables.
                      </span>
                    </div>
                  </div>
                )}
              </Panel>
              <Panel
                title="Estado de las fuentes"
                kicker="OBSERVABILIDAD"
                icon={<ShieldCheck />}
              >
                {data.sources.map((source) => (
                  <a
                    className="source-row"
                    href={source.url}
                    target="_blank"
                    rel="noreferrer"
                    key={source.id}
                  >
                    <span
                      className={`source-dot ${source.state.toLowerCase()}`}
                    />
                    <div>
                      <b>{source.name}</b>
                      <small>{source.note}</small>
                    </div>
                    <strong>{source.state}</strong>
                  </a>
                ))}
              </Panel>
            </div>
          </>
        )}

        {active === "Motion & 3D" && (
          <>
            <SectionIntro
              eyebrow="VISUALIZACIÓN CUANTITATIVA INMERSIVA"
              title="Motion charts y 3D que explican datos, no los decoran"
              description="Nuevas vistas dinámicas con datos observados y calculados trazables. Cada eje, color, transformación, fecha, fuente y límite aparece debajo del gráfico."
              decision="Detectar divergencias entre precios, poder adquisitivo, actividad, consumo PyME y recursos provinciales sin confundir movimiento con predicción."
            />
            <Motion3DSuite data={data} reducedMotion={reducedMotion} />
          </>
        )}

        {active === "PyME y Provincias" && (
          <>
            <SectionIntro
              eyebrow="ECONOMÍA REAL Y TERRITORIO"
              title="Ventas, fechas especiales, provincias y turismo con comparadores honestos"
              description="Nueva lectura integrada de CAME, OPEX, Recursos de Origen Nacional y ocupación hotelera. Cada gráfico explica sus ejes, colores, alcance y límite antes de sugerir una decisión."
              decision="Separar crecimiento nominal, crecimiento real, cantidad de personas y valor exportado para no decidir con variables incompatibles."
            />
            <TerritorialPanel data={data} />
          </>
        )}

        {active === "Carga de datos" && (
          <>
            <SectionIntro
              eyebrow="MESA DE EVIDENCIA LOCAL"
              title="Subí Excel, CSV o PDF y convertí datos propios en análisis controlado"
              description="CSV y Excel se leen dentro del navegador, permiten elegir hoja, ejes y hasta tres líneas, generan un motion chart y habilitan proyección sólo con muestra mensual suficiente. Los PDF se leen como texto sin fabricar tablas."
              decision="Analizar información propia con unidad y fuente explícitas, sin mezclarla automáticamente con las series oficiales."
            />
            <Suspense fallback={<div className="splash"><span>Leyendo el motor local de Excel y PDF…</span></div>}><UploadWorkbench /></Suspense>
          </>
        )}

        {active === "Inteligencia y Alertas" && (
          <>
            <SectionIntro
              eyebrow="APIS · IA CUANTITATIVA · ALERTAS · REPORTES"
              title="Motor de inteligencia con reglas y evidencia visibles"
              description="Integra el estado de las APIs macroeconómicas, modelos habilitados, anomalías robustas, alertas operativas y exportación de reportes. Cada conclusión muestra la condición que la generó."
              decision="Distinguir una alerta automática de una inferencia causal y conservar siempre la decisión final en una persona responsable."
            />
            <IntelligencePanel data={data} />
          </>
        )}

        {active === "Calendario" && (
          <Panel
            title="Calendario económico y disparadores de actualización"
            kicker="PRÓXIMAS PUBLICACIONES"
            icon={<CalendarDays />}
            wide
          >
            <div className="engine-status">
              <div className="engine-pulse"><span/><b>MOTOR PROGRAMADO</b></div>
              <div><small>VERIFICACIÓN AUTOMÁTICA</small><b>Cada hora; la interfaz vuelve a consultar cada 5 minutos</b></div>
              <div><small>ÚLTIMA CONSOLIDACIÓN</small><b>{formatDate(data.updatedAt)}</b></div>
              <div><small>PRÓXIMA EJECUCIÓN ESTIMADA</small><b>{formatDate(data.nextUpdateAt)}</b></div>
              <p>El motor consulta APIs y páginas institucionales permitidas, conserva el último dato válido si una fuente falla y nunca reemplaza un faltante con un valor inventado. “LIVE” describe la conexión, no convierte una encuesta o un modelo en dato oficial.</p>
            </div>
            <div className="next-release">
              <CalendarDays />
              <div><small>PRÓXIMO INFORME ESTADÍSTICO REGISTRADO</small><b>{data.calendar.find((event) => event.status === "PRÓXIMO")?.title ?? "Sin publicación próxima cargada"}</b><span>{data.calendar.find((event) => event.status === "PRÓXIMO")?.date ?? "s/d"} · {data.calendar.find((event) => event.status === "PRÓXIMO")?.agency ?? "s/d"}</span></div>
            </div>
            {data.calendar.map((event) => (
              <a
                href={event.url}
                target="_blank"
                rel="noreferrer"
                className="event"
                key={event.id}
              >
                <time>
                  <b>
                    {new Date(`${event.date}T12:00:00Z`).toLocaleDateString(
                      "es-AR",
                      { day: "2-digit", month: "short" },
                    )}
                  </b>
                  <span>{event.time}</span>
                </time>
                <div>
                  <small>
                    {event.agency} · {event.period}
                  </small>
                  <h3>{event.title}</h3>
                </div>
                <span className={`event-state ${event.status.toLowerCase()}`}>
                  {event.status}
                </span>
                <ExternalLink />
              </a>
            ))}
          </Panel>
        )}

        {active === "Fuentes" && (
          <Panel
            title="Linaje, estado y metodología"
            kicker="CONTRATO DE DATOS"
            icon={<Database />}
            wide
          >
            <div className="quality-hero">
              <div>
                <small>ÍNDICE OPERATIVO DE CALIDAD</small>
                <strong>
                  {qualityScore}
                  <i>/100</i>
                </strong>
              </div>
              <p>
                Promedio transparente del estado de las fuentes: LIVE 100,
                actualizado 90, parcial 65, demorado 45 y error 0. No mide la
                calidad metodológica del organismo.
              </p>
            </div>
            <div className="data-ledger">
              <div className="ledger-head">
                <span>Indicador</span>
                <span>Valor y período</span>
                <span>Unidad / definición</span>
                <span>Clase</span>
                <span>Fuente</span>
              </div>
              {data.metrics.map((item) => (
                <div className="ledger-row" key={item.id}>
                  <b>{item.label}</b>
                  <span>
                    {item.display}
                    <small>{item.period}</small>
                  </span>
                  <span>
                    {item.unit}
                    <small>
                      {item.definition ??
                        "Definición disponible en la fuente oficial"}
                    </small>
                  </span>
                  <em className={`data-badge ${item.status.toLowerCase()}`}>
                    {item.status}
                  </em>
                  <a href={item.sourceUrl} target="_blank" rel="noreferrer">
                    {item.source}
                    <ExternalLink />
                  </a>
                </div>
              ))}
            </div>
            <div className="sources-grid">
              {data.sources.map((source) => (
                <article key={source.id}>
                  <div className="source-head">
                    <span
                      className={`source-dot ${source.state.toLowerCase()}`}
                    />
                    <b>{source.name}</b>
                  </div>
                  <p>{source.note}</p>
                  <small>Verificado: {formatDate(source.checkedAt)}</small>
                  <a href={source.url} target="_blank" rel="noreferrer">
                    Abrir fuente oficial <ExternalLink />
                  </a>
                </article>
              ))}
            </div>
            <div className="equation-grid">
              <article>
                <small>PODER DE COMPRA REAL</small>
                <code>(1 + ΔIngreso) / (1 + IPC) − 1</code>
                <p>Evita restar porcentajes cuando el cambio es grande.</p>
              </article>
              <article>
                <small>COBERTURA DE INTERESES</small>
                <code>(Ingresos − Costos) / Interés</code>
                <p>Capacidad operativa para absorber el costo financiero.</p>
              </article>
              <article>
                <small>PUNTO DE EQUILIBRIO</small>
                <code>Costos ajustados + Servicio de deuda</code>
                <p>Ingreso nominal mínimo del escenario.</p>
              </article>
              <article>
                <small>EXPOSICIÓN CAMBIARIA</small>
                <code>Costos × % importado × ΔFX</code>
                <p>Impacto directo simplificado del dólar.</p>
              </article>
              <article>
                <small>MONTE CARLO</small>
                <code>Yˢ = f(πˢ, ΔFXˢ, ΔQˢ, iˢ)</code>
                <p>
                  Dos mil escenarios reproducibles con supuestos visibles; no
                  pronóstico.
                </p>
              </article>
              <article>
                <small>EXPECTED SHORTFALL 10%</small>
                <code>E[Y | Y ≤ P10]</code>
                <p>Resultado medio dentro del peor 10% de escenarios.</p>
              </article>
              <article>
                <small>ERROR DE MODELO</small>
                <code>MAE · RMSE · sMAPE · Sesgo</code>
                <p>
                  Naive, Deriva y Holt amortiguado compiten fuera de muestra.
                </p>
              </article>
              <article>
                <small>BANDAS PREDICTIVAS</small>
                <code>Pq[ŷₜ₊ₕ + Σ ε*]</code>
                <p>
                  Percentiles de 10.000 trayectorias de residuos centrados;
                  provisionales hasta calibrar cobertura.
                </p>
              </article>
            </div>
            <div className="methodology">
              <Gauge />
              <p>{data.methodology}</p>
            </div>
          </Panel>
        )}
      </main>

      <section className="sales-cta">
        <div>
          <span>MONTIEL · DECISION SYSTEMS</span>
          <h2>
            Tu empresa no necesita otro informe.
            <br />
            Necesita un cerebro de decisión.
          </h2>
          <p>
            Diseñamos tableros automáticos, inteligentes y auditables para
            PyMEs, profesionales y organizaciones.
          </p>
        </div>
        <div className="sales-actions">
          <a
            href="https://wa.me/543624000857?text=Quiero%20cotizar%20mi%20dashboard%20inteligente"
            target="_blank"
            rel="noreferrer"
          >
            Solicitar diagnóstico <ChevronRight />
          </a>
          <small>
            Datos · Automatización · Alertas · Proyecciones · Decisión
          </small>
        </div>
      </section>

      <footer>
        <div>
          <strong>Marcos Isaías Montiel</strong>
          <span>Dr. Economía y Finanzas</span>
        </div>
        <div className="footer-links">
          <a href="tel:+543624000857">Cel. 3624 000857</a>
          <a
            href="https://www.linkedin.com/in/marcosisaiasmontiel-75063164/"
            target="_blank"
            rel="noreferrer"
          >
            LinkedIn <ExternalLink />
          </a>
        </div>
        <span>Datos oficiales · Modelos auditables · Decisiones humanas</span>
      </footer>
    </div>
  );
}

export default App;
